import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateNavigator, { PastDateBanner } from './DateNavigator';
import BriefingCard from './BriefingCard';
import EvidenceModal from './EvidenceModal';
import { OPEN_MORNING_BRIEFING_EVENT } from './morningBriefingEvent';
/*
 * `explainComposition` 은 더 이상 이 화면이 안 부른다 (2026-09-01 QA32).
 * 함수는 그대로 살아 있고 지난 리포트가 계속 쓴다 — 화면에서만 뺐다.
 */
import { QUOTA, resolveCenterSize, quotaNoteOf } from './briefingPolicy';
import { toOrderViews } from './adapters';
import {
    withValues,
    summarizeValue,
    prepaidContributors,
    expectedContributors,
} from './briefingValue';
import ValueBreakdownModal, { ValueTrigger } from './ValueBreakdownModal';
import {
    itemPrepaidRows,
    itemExpectedRows,
    PREPAID_DEFINITION,
    EXPECTED_DEFINITION,
    PREPAID_NOTES,
    expectedNotes,
    UNPRICED_REASON,
} from './valueBreakdown';
import OrderTracker from './OrderTracker';
import CoachTaskMobilePreview from './CoachTaskMobilePreview';
import { useOrderConfirm } from './OrderConfirmModal';
import { BriefingItemDto, CreateOrderRequest } from '../../types/opsBriefing';
import { useOpsContext } from './useOpsContext';
import {
    useOpsBriefingQuery,
    useOpsCoachesQuery,
    useOpsOrdersQuery,
    useSetOpsBriefingItemDoneMutation,
    useSetOpsBriefingItemStatusMutation,
    useOpsOnboardingQuery,
    useOpsCoachSettlingQuery,
    useOpsCoachOnboardingQuery,
} from '../../services/useOpsBriefingService';
import { OpsLoading, OpsError, OpsEmpty, OpsMockBadge } from './OpsStates';
import { isOpsBriefingMock, postOpsBriefingActionAPI } from '../../api/opsBriefing';
import { analyzeAll, NO_CENTER_AVG } from './coachAnalysis';
import {
    composeBriefing,
    pickCoachInsight,
    BRIEFING_FOCUS_COUNT,
    MAX_SETUP_SLOTS,
} from './briefingComposition';
import { resolveSetupTasks } from './setupTasks';
import { resolveOnboarding } from './onboarding';
import {
    readCheckedSetupTasks,
    readHiddenSetupTasks,
    toggleCheckedSetupTask,
    hideSetupTask,
    unhideSetupTask,
    SETUP_HIDE_DAYS,
} from './setupTaskState';
import SetupTaskCard from './SetupTaskCard';
import OnboardingBriefingScreen from './OnboardingBriefingScreen';
import CoachOnboardingCard from './CoachOnboardingCard';
import { toCardView, sortCards } from './coachOnboarding';
import { memberNav, MemberNeed, MemberActionTarget } from './coachOnboardingNav';
import {
    resolveCoachActions,
    actionStateOf,
    withOptimistic,
    persistCoachAction,
    coachActionToast,
    CoachActionType,
} from './coachOnboardingActions';
import CoachInsightCard from './CoachInsightCard';
import CoachDataDrawer from './CoachDataDrawer';
import BriefingDetailDrawer from './BriefingDetailDrawer';
import CoachReportSheet from './CoachReportSheet';
import { useGiftSendFlow } from './useGiftSendFlow';

dayjs.locale('ko');

/**
 * 오늘의 브리핑 — 운영 리포트 본체.
 *
 * 정보 위계 (DAILY_OPS_BRIEFING_UI_SPEC.md 3장):
 *   ① 한 줄 요약 → ② 오늘 챙길 일 → ③ 지시 및 진행 현황
 *
 * 강사 현황·어제 요약 블록은 걷어냈다. 매일 아침 보는 화면에서
 * "지금 뭘 해야 하나"와 무관한 숫자는 시선만 나눠 가진다.
 * 강사 지표는 강사 현황 탭에서 훨씬 깊게 볼 수 있다.
 *
 * ─────────────────────────────────────────────────────────────
 * 버튼은 전부 서버에 남습니다
 * ─────────────────────────────────────────────────────────────
 * 예전에는 체크·되돌리기·넘어가기가 전부 로컬 state 만 바꿨습니다.
 * 새로고침하면 되돌아갔고, 카드 문구는 "강사에게 전달됩니다"라고 약속했습니다.
 * 원장님이 회원에게 전화를 걸지 말지 정하는 화면에서 그건 치명적입니다.
 *
 * 지금은 두 갈래로 서버에 씁니다.
 *   · 강사에게 보낼 수 있는 건(회원 항목 + 담당 강사) → POST /orders
 *     서버가 지시를 만들고 그 브리핑 항목을 ACTED 로 바꿉니다.
 *   · 그 밖의 건(원장 직접 연락 · 강사 항목 · 센터 항목) → PATCH /history/:date/items/:index
 *     지시가 아니라 "처리했다"는 기록만 남습니다.
 *
 * ⚠️ 두 번째 경로는 **items 배열의 위치(index)** 로 항목을 찾습니다.
 *    화면에서 항목을 걸러내거나 정렬을 바꾸면 엉뚱한 항목이 완료 처리됩니다.
 *    그래서 **표시 순서와 서버 위치를 갈라 둡니다** —
 *    표시 편성(briefingComposition)은 항목마다 원래 위치(index)를 달고 다니고,
 *    handleAction·handleMarkDone·handleUndo 는 정렬 전 `items` 에서 findIndex 로
 *    위치를 구합니다. 이 규칙이 깨지면 원장님이 A 를 눌렀는데 B 가 완료됩니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 목록은 카드의 데이터 영역, 요청은 상세에서 (2026-08 변경)
 * ─────────────────────────────────────────────────────────────
 * 한때 목록을 한 줄짜리 압축 요약(BriefingSummaryRow)으로 바꿨습니다.
 * 스물아홉 건이 올라온 날 어느 것도 안 읽히던 문제는 풀렸지만, 그 대신
 * 판단에 쓰는 수치가 12px 로 눌려 **데이터를 크게 보는 화면이 아니게** 됐습니다.
 * 대표 피드백으로 되돌립니다 — 목록은 **BriefingCard 의 데이터 영역 그대로**입니다.
 * (최근 60일·이전 60일 수치 블록, 우측 지표 패널, 8주 추이, 판단 문장과 근거)
 *
 * 대신 **강사에게 요청하는 부분만** 상세로 뺐습니다.
 * 오늘 할 일 배너·추천 문구·지시 버튼은 카드를 눌러야 열리는 드로어에만 있습니다
 * (BriefingCard 의 `actions` prop — 카드를 두 벌로 포크하지 않습니다).
 *
 * 그리고 기본 화면에는 위험 순 5건만 세웁니다 (회원 4 + 강사 1).
 * 나머지는 "외 N건 더 보기" 로 접습니다 — 지운 것이 아니라 접은 것입니다.
 */

const CARD_BOX =
    'w-full rounded-[12px] border border-solid border-[#D4DBE8] bg-white';

/**
 * 선물 보내기 — 강사 지시도, 완료 기록도 아니다. **돈이 나간다.**
 *
 * 이 액션 코드만은 handleAction 의 다른 갈래에 절대 닿으면 안 됩니다.
 * (지시가 만들어지면 강사가 없던 일을 하고, 완료로 기록되면 보내지도 않은 선물이
 *  처리된 것으로 남습니다)
 */
export const GIFT_ACTION_CODE = 'GIFT';

/**
 * 이 항목을 강사 지시로 만들 수 있는가.
 *
 * 서버는 (memberId, coachId) 조합이 실제 담당 관계인지 확인합니다.
 * 회원 항목이 아니거나 담당 강사가 안 붙어 있으면 지시를 만들 수 없습니다.
 * CALL 은 "대표님이 직접 연락"이라 강사에게 넘기면 뜻이 뒤집힙니다.
 * GIFT 는 지시가 아니라 발송입니다 — 여기서 true 가 되면 확인 창이 강사 지시로 열립니다.
 *
 * ⚠️ 여기서 막는 것만으로는 부족합니다. handleAction 이 GIFT 를 **먼저** 갈라내지
 *    않으면 아래 setItemDone 으로 떨어져 "완료" 기록이 남습니다.
 */
export const canCreateOrder = (item: BriefingItemDto, actionCode: string): boolean =>
    item.subjectType === 'MEMBER' &&
    !!item.subjectId &&
    !!item.relatedCoachId &&
    actionCode !== 'CALL' &&
    actionCode !== GIFT_ACTION_CODE;

/**
 * 지시에 실을 금액 — **이 건에 걸린 돈 전부**.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 서버에 저장되는 값이라 계약(`valueAmount`·`valueKind`)은 그대로 둡니다
 * ─────────────────────────────────────────────────────────────
 * 지시는 "이 회원을 챙겨라"이고, 걸린 돈은 미사용 선수금과 재등록 기대를
 * 합친 것입니다. 화면 합계와 달리 여기서는 **한 건 안에서만** 더하므로
 * 이중계상이 생기지 않습니다 (합계는 회원당 기대를 한 번만 셉니다).
 *
 * 2026-08-31 정책 변경으로 **이탈 위험·주의 회원의 지시 금액이 줄어듭니다** —
 * 그 자리의 재등록 기대가 0 이 되어 미사용 선수금만 남기 때문입니다.
 * 이미 저장된 지시의 금액은 그대로입니다(과거를 소급해 고치지 않습니다).
 *
 * `valueKind` 는 지시 트래커의 색·문구가 읽는 값입니다. 새 정의에서는
 * 「기대가 실린 건인가」로 읽습니다 — GAIN 이면 재등록으로 새로 들어올 돈이
 * 섞여 있고, DEFEND 면 전액이 이미 받아 둔 선수금입니다.
 */
const orderValueOf = (item: BriefingItemDto): Pick<CreateOrderRequest, 'valueAmount' | 'valueKind'> => {
    const v = item.value;
    if (!v || (v.prepaid == null && v.expected == null)) {
        return { valueAmount: null, valueKind: 'DEFEND' };
    }
    /*
     * `valueKind` 는 두 값 중 **큰 쪽의 성격**으로 정한다.
     * 「기대가 조금이라도 있으면 GAIN」으로 두면, 선수금 80만 + 기대 5만인 지시가
     * 트래커에서 85만원 전체가 「확보 가능」으로 읽힌다. 두 축을 한 필드에 저장하는
     * 계약이라 손실은 피할 수 없고, 그렇다면 대부분이 어느 쪽인지를 말하는 것이 덜 틀린다.
     * (회원 현황의 같은 규칙과 맞춰야 두 화면이 만든 지시가 트래커에서 갈리지 않는다)
     */
    return {
        valueAmount: (v.prepaid ?? 0) + (v.expected ?? 0),
        valueKind: (v.expected ?? 0) > (v.prepaid ?? 0) ? 'GAIN' : 'DEFEND',
    };
};

/** 토스트 한 줄 — 되돌릴 수 있는 동작이면 그 자리에 되돌리기를 함께 단다 */
type Toast = { text: string; undo?: { label: string; run: () => void } };


/**
 * 토스트 한 장 — **신규 온보딩 화면과 본 화면이 같은 것을 쓴다.**
 *
 * 화면 분기가 늘면서 토스트 마크업을 분기마다 복사하게 됐다. 복사본은
 * 조용히 낡는다 — 되돌리기 버튼이 한쪽에만 남는 식이다.
 */
const BriefingToast = ({ toast, onUndo }: { toast: Toast; onUndo: () => void }) => (
    <div
        className="fixed left-1/2 flex items-center rounded-[10px] px-[20px] py-[14px]"
        style={{
            bottom: 40,
            transform: 'translateX(-50%)',
            background: '#1A305D',
            color: '#FFFFFF',
            fontSize: 16,
            zIndex: 1000,
            gap: 16,
            boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
        }}
    >
        <span>{toast.text}</span>
        {toast.undo && (
            <button
                type="button"
                onClick={() => {
                    const run = toast.undo?.run;
                    onUndo();
                    run?.();
                }}
                style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#9DC2FF',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                    padding: 0,
                    flex: 'none',
                }}
            >
                {toast.undo.label}
            </button>
        )}
    </div>
);
const OpsBriefingTodayPage = () => {
    const navigate = useNavigate();
    const ctx = useOpsContext();
    const [toast, setToast] = useState<Toast | null>(null);
    const [rawItemId, setRawItemId] = useState<string | null>(null);
    const [showCoachApp, setShowCoachApp] = useState(false);
    const [date, setDate] = useState<Dayjs>(() => dayjs().startOf('day'));
    /**
     * 상세를 열어 둔 브리핑 항목.
     *
     * itemId 로 들고 있는다 — 항목 객체를 복사해 두면 지시를 보낸 뒤
     * 새로 받아 온 목록과 드로어 안의 값이 어긋난다 (회원 현황과 같은 규칙).
     */
    const [openItemId, setOpenItemId] = useState<string | null>(null);
    /** 강사 인사이트 줄을 눌러 연 강사 상세 */
    const [openCoachId, setOpenCoachId] = useState<string | null>(null);
    /** "외 N건 더 보기" 를 펼쳤나 */
    /** 가린 항목을 다시 꺼내 보고 있나 */
    const [showDismissed, setShowDismissed] = useState(false);
    /**
     * 「왜 이 금액인가」 팝업 (2026-09-01 QA31).
     *
     * 숫자를 복사해 두지 않고 **무엇을 여는지만** 들고 있다. 열어 둔 사이에
     * 목록을 다시 받아 오면 복사본은 낡고, 팝업이 헤더와 다른 값을 말하게 된다.
     */
    const [valueModal, setValueModal] = useState<
        | { kind: 'TOTAL'; axis: 'PREPAID' | 'EXPECTED' }
        /** 카드 금액 태그 — 그 한 건의 근거 */
        | { kind: 'ITEM'; itemId: string; axis: 'PREPAID' | 'EXPECTED' }
        | null
    >(null);
    const isToday = date.isSame(dayjs().startOf('day'), 'day');
    const dateKey = date.format('YYYY-MM-DD');

    const { data, isLoading, isError, refetch } = useOpsBriefingQuery(ctx, dateKey, ctx.ready);
    /* 강사 앱 미리보기에 실을 실제 지시. OrderTracker 와 같은 캐시를 씁니다 */
    const { data: orders, isLoading: ordersLoading } = useOpsOrdersQuery(ctx, ctx.ready);
    /*
     * 강사 인사이트 한 건을 편성하려고 강사 현황 탭과 **같은 조회**를 씁니다.
     * 캐시 키가 같아서 강사 탭을 이미 봤다면 요청이 더 나가지 않고,
     * 무엇보다 두 화면의 "오늘 챙길 강사" 판정이 갈리지 않습니다.
     */
    const { data: coachData } = useOpsCoachesQuery(ctx, date.format('YYYY-MM'), ctx.ready);
    /*
     * 세팅 집계 2종 (백엔드 3차 B5·B8).
     *
     * 🔴 **실패해도 화면이 깨지지 않습니다.** API 계층이 실패를 null 로 바꿉니다
     *    (`api/opsBriefing.optional`). 백엔드보다 웹이 먼저 배포될 수 있어서,
     *    라우트가 없을 때 「불러오지 못했습니다」가 뜨면 멀쩡하던 브리핑이
     *    고장 나 보입니다 — 있지도 않던 카드 때문에요.
     *    그래서 `isError` 를 보지 않고 **값이 없으면 안 그립니다.**
     *
     * ⚠️ 날짜를 안 넘깁니다. 서버가 `?date=` 를 아예 안 받습니다(현재 상태 집계).
     *    그래서 날짜를 옮겨도 이 두 카드는 같은 값을 보여줍니다 — 의도된 것입니다.
     */
    const { data: onboarding } = useOpsOnboardingQuery(ctx, ctx.ready);
    /*
     * 강사별 온보딩 집계 — 신규·과도기 화면의 원천 (2026-09-01 대표 시안).
     *
     * 🔴 **없으면 null 이고 그게 지금의 정상이다.** 백엔드가 이 계약으로
     *    병렬 구현 중이라 배포 전에는 404 가 온다. null 이면 아래 분기가
     *    전부 거짓이 되어 **기존 축 카드 화면이 그대로** 뜬다.
     *    서버가 먼저 나가든 웹이 먼저 나가든 화면이 비지 않는다.
     */
    const { data: coachOnboarding, refetch: refetchCoachOnboarding } = useOpsCoachOnboardingQuery(
        ctx,
        ctx.ready
    );
    /*
     * 방금 누른 처리 표시(체크·오늘만 가리기).
     * 서버 이력(`coachOnboarding.actions`)이 정본이고 이건 그 위에 얹는 임시 값이다 —
     * 응답이 오기 전에도 카드가 즉시 반응하게 한다. 저장이 성공하면 `refetch` 로
     * 서버 이력을 다시 읽어 같은 상태가 정본에서 온다.
     *
     * 🔴 예전에는 여기서 localStorage 에만 적었다. 서버가 `actions` 를 주는 센터는
     *    그 기록을 읽지 않으므로(서버가 이긴다) 체크가 토스트만 남기고 아무 일도
     *    안 했다 — 2026-09-10 범계점 신고. 신규 센터 화면과 같은 순서로 맞춘다.
     */
    const [pendingCoachActions, setPendingCoachActions] = useState<
        Array<{ coachId: string; type: CoachActionType }>
    >([]);
    /* 저장 중인 표시(강사|종류) — 연타를 한 번으로 만든다 */
    const inFlightCoachActions = useRef<Set<string>>(new Set());
    const serverBackedRef = useRef(false);
    /**
     * 서버 이력을 다시 읽는다. **못 읽으면 던진다** — 부르는 쪽이 임시 값을 거둘지 정한다.
     * react-query 의 refetch 는 실패해도 resolve 하므로 status 를 봐야 한다.
     */
    const refreshCoachOnboarding = async () => {
        const result = (await refetchCoachOnboarding?.()) as
            | { status?: string; data?: unknown }
            | undefined;
        /* 훅이 실패를 status 로 알린다(직전 data 는 유지). data 가 비어도 정본으로 갈아탈 수 없다 */
        if (result && (result.status === 'error' || result.data == null)) {
            throw new Error('coach-onboarding refetch failed');
        }
    };
    const { data: coachSettling } = useOpsCoachSettlingQuery(ctx, ctx.ready);

    /**
     * 🔴 **상세 섹션 두 개(센터 세팅 5단계 진행바 · 강사 정착 강사 목록)를 뺐다**
     *    (2026-08-31 QA8).
     *
     * 같은 데이터가 이제 목록 위 **합성 과제 카드**로 브리핑에 들어간다.
     * 아래에 상세 섹션까지 통째로 그리면 같은 이야기를 한 화면에서 두 번 하게 되고,
     * 브리핑이 다시 「전부 늘어놓는 화면」이 된다.
     *
     * ⚠️ 판정 모듈(`onboarding.ts`·`coachSettling.ts`)과 조회 훅은 **그대로** 쓴다.
     *    합성 카드(`resolveSetupTasks`)가 그 값으로 만들어진다.
     *    지운 것은 화면 렌더뿐이다.
     */

    const setItemDone = useSetOpsBriefingItemDoneMutation(ctx, dateKey);
    /** 가리기 / 되돌리기 — 낙관 반영과 롤백은 훅이 맡는다 */
    const setItemStatus = useSetOpsBriefingItemStatusMutation(ctx, dateKey);
    /*
     * 지시는 확인 창을 지나야만 나갑니다.
     * 회원 현황 화면과 **같은 훅**을 씁니다 — 두 곳이 다르게 동작하면 원장님이 헷갈립니다.
     */
    const confirm = useOrderConfirm(ctx, {
        notify: (msg) => showMessage(msg),
        tracker: '아래 진행 현황에 남았습니다',
    });
    /*
     * 선물 발송 — 회원 현황 화면과 **같은 훅**을 씁니다.
     *
     * 두 화면이 각자 창을 들면 멱등키가 갈리고, 그러면 같은 회원에게 두 번 나갑니다.
     * 키는 화면이 아니라 의도(센터·회원·상품·날짜)에서 나옵니다 — giftSendKey.ts 참고.
     */
    const gift = useGiftSendFlow(ctx, { notify: (msg) => showMessage(msg) });

    /*
     * 서버 응답을 그대로 그립니다.
     * 예전에는 useState 로 복사해 두고 로컬에서 먼저 바꿨는데,
     * 그러면 새로고침에 되돌아가고 index 도 흔들립니다.
     * 즉각 반응은 훅의 낙관적 업데이트가 담당합니다.
     */
    /*
     * ⚠️ useMemo 로 감쌉니다. `data?.items ?? []` 를 그대로 쓰면 응답이 없는 동안
     * 매 렌더마다 **새 빈 배열**이 만들어져, 이 값을 의존성으로 쓰는 편성 계산이
     * 매번 다시 돕니다 (강사 판정까지 딸려 돕니다).
     *
     * 🔴 **금액은 여기서 붙입니다** (`withValues`).
     *
     * 서버는 계약대로 `value` 를 null 로 주고 단가 원본(`membership`)만 보냅니다
     * (types/opsBriefing.ts — "서버는 계산하지 마세요"). 그걸 계산기에 태우는
     * 곳이 없어서 이 화면의 금액이 **전부 비어 있었습니다** — 헤더의 「지켜야 할
     * 매출」·「확보 가능한 매출」, 카드의 금액 태그, 지시에 실리는 금액까지 전부.
     *
     * 아래 모든 소비자가 `item.value` 하나만 읽으므로 입구에서 한 번 채우면 됩니다.
     * 화면 여러 곳에서 각자 계산하지 않는 것이 핵심입니다 — 그러면 같은 항목의
     * 카드 금액과 헤더 합계가 갈립니다.
     */
    const items: BriefingItemDto[] = useMemo(() => withValues(data?.items ?? []), [data]);

    /**
     * 활성 회원 수.
     *
     * ⚠️ `?? 0` 을 쓰면 안 됩니다. 서버는 브리핑이 생성되지 않은 날짜에 null 을 줍니다.
     * 그 0 이 resolveCenterSize(0) → 'SMALL' 로 흘러 화면에
     * "소형 센터(활성 0명) 기준 하루 최대 3건" 이라는 없는 사실을 찍었습니다.
     */
    const activeMembers = data?.activeMembers ?? null;
    /**
     * 편성 결과 — 후보가 몇이었고 무엇이 빠졌나.
     *
     * ⚠️ 여기서도 `?? 0` 같은 채우기를 하면 안 됩니다. 편성 기록이 없는 날은 null 이고,
     * null 은 "모른다"입니다. 0 으로 읽으면 "상한 0건 센터"가 화면에 찍힙니다.
     */
    const composition = data?.composition ?? null;
    const yesterday = data?.yesterdayOrders ?? { total: 0, done: 0 };
    /** 과거를 보고 있을 때 "오늘"이라고 쓰면 거짓말이 된다 */
    const dayWord = isToday ? '오늘' : date.format('M월 D일');

    /* ── 표시 편성 ────────────────────────────────────────────
     *
     * 서버가 올린 것 전부를 세로로 늘어놓으면 어느 것도 안 읽힙니다.
     * 위험 순으로 다섯 건만 세우고 나머지는 접습니다 (briefingComposition).
     *
     * ⚠️ 여기서 만든 순서를 **완료 기록에 쓰지 않습니다.**
     *    완료 기록 API 는 서버 items 배열의 위치(index)로 항목을 찾습니다.
     *    아래 handleAction·handleMarkDone·handleUndo 는 전부 정렬 전 `items` 에서
     *    findIndex 로 위치를 구합니다 — 이 규칙이 깨지면 엉뚱한 항목이 완료됩니다.
     * ────────────────────────────────────────────────────── */

    /*
     * 강사 인사이트 한 건.
     * 판정은 강사 현황 탭과 같은 함수(analyzeCoach → pickCoachesToAct)를 씁니다.
     * 센터 평균을 못 받았으면 전부 null 인 기준선으로 떨어져 등급이 NA 가 되고,
     * 그러면 편성 대상이 없어 회원 항목이 다섯 건 올라갑니다 —
     * 목 상수로 메우지 않습니다 (남의 센터 기준으로 강사를 지목하게 됩니다).
     */
    const coachRows = useMemo(() => coachData?.coaches ?? [], [coachData]);
    const centerAvg = coachData?.centerAvg ?? NO_CENTER_AVG;
    /**
     * ─────────────────────────────────────────────────────────────
     * 🔴 강사 현황에서 **가려 둔 강사는 브리핑에도 안 나온다** (2026-09-02 QA38)
     * ─────────────────────────────────────────────────────────────
     * 영구 가리기는 강사 현황 탭에만 있었고(`useSetOpsCoachHiddenMutation`),
     * 아침 브리핑의 강사 온보딩 카드는 그것을 몰랐다. 그래서 원장님이
     * 「이 강사는 그만 보겠다」고 가려 둔 사람이 **다음 날 아침 다시** 떴다.
     * 카드의 가리기는 `HIDE_TODAY`(그날만)이라 영구히 치울 방법이 없었다.
     *
     * ⚠️ `CoachOnboardingRow` 에는 `hidden` 이 없다. 그래서 강사 현황과 **같은
     *    조회**(`useOpsCoachesQuery`, 캐시 키까지 동일)에서 교집합으로 거른다.
     *    서버가 실어 주는 것이 맞아서 핸드오프로 넘겼다
     *    (`BACKEND_HANDOFF_OPS_COACH_ONBOARDING_HIDDEN.md`) — 그러면 아침 팝업도
     *    같이 고쳐진다. 팝업은 이 조회를 안 해서 지금은 못 거른다.
     *
     * ⚠️ **가리기 행은 두 배열에 나뉘어 있다.** 강사 현황이 `coaches` 와
     *    `closing` 의 합집합을 보는 것과 같은 이유다 — 한쪽에만 남는 강사가 있다.
     */
    const hiddenCoachIds = useMemo(() => {
        const ids = new Set<string>();
        (coachData?.coaches ?? []).forEach((c) => {
            if (c.hidden === true) ids.add(c.coachId);
        });
        (coachData?.closing ?? []).forEach((c) => {
            if (c.hidden === true) ids.add(c.coachId);
        });
        return ids;
    }, [coachData]);
    /**
     * 브리핑이 다룰 강사 명단.
     *
     * 🔴 **전부 가려 두면 거르지 않는다.** 강사별 온보딩 화면은 명단이 비면
     *    「등록된 강사가 없습니다」로 빠지고, 그 순간 원장님은 데이터가
     *    사라진 줄 안다 — 강사 현황이 `allCoaches` 를 보는 것과 같은 판단이다.
     *    (오래된 미수락은 여기서 안 거른다. 그건 명단에는 남되 「오늘 챙길 일」
     *     에서만 빠진다 — `coachOnboarding.isStaleAccept`)
     */
    const onboardingCoaches = useMemo(() => {
        const all = coachOnboarding?.coaches ?? [];
        if (hiddenCoachIds.size === 0) return all;
        const shown = all.filter((c) => !hiddenCoachIds.has(c.coachId));
        return shown.length > 0 ? shown : all;
    }, [coachOnboarding, hiddenCoachIds]);
    const coachAnalyses = useMemo(
        () => analyzeAll(coachRows, centerAvg),
        [coachRows, centerAvg]
    );
    const coachInsight = useMemo(
        () => pickCoachInsight(coachAnalyses, items),
        [coachAnalyses, items]
    );

    /*
     * 세팅·정착 과제 카드 (2026-08-28 대표 지시).
     * 데이터가 없으면(404·구서버) 빈 배열이라 기존 화면 그대로다.
     */
    /*
     * ─────────────────────────────────────────────────────────────
     * 세팅 과제의 체크·가리기 — **이 브라우저에만** 남는다 (2026-09-01 QA29)
     * ─────────────────────────────────────────────────────────────
     * 세팅 과제는 서버 브리핑 항목이 아니라 `itemId` 가 없다. 그래서 회원
     * 카드처럼 `PATCH /items/:itemId` 로 보낼 수가 없고, localStorage 로 간다
     * (한계와 서버화 계획은 `setupTaskState.ts` 머리말에).
     */
    const [checkedSetup, setCheckedSetup] = useState<Set<string>>(() =>
        readCheckedSetupTasks()
    );
    const [hiddenSetup, setHiddenSetup] = useState<Set<string>>(() => readHiddenSetupTasks());

    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 「아직 안 만들어졌다」와 「올릴 것이 없었다」는 다르다
     * ═══════════════════════════════════════════════════════════
     * (2026-09-02 대표 QA ② — 정착 센터에 신규 센터 화면이 나왔다)
     *
     * 생성 배치는 아침 08:04 에 돈다. 대표가 본 07:53 에는 그날 문서가
     * **아직 없었다**(items 0건). 그런데 자리 보충 규칙은 「회원 카드 0건」을
     * 신규 센터로 읽어서 세팅 카드 상한을 풀어 버린다(QA29 규칙).
     * 결과: **매일 자정~08:04 사이에는 모든 센터가 신규 센터 화면**이 됐다.
     *
     * 판별은 이미 계약에 있다 — `activeMembers` 는 **문서가 없는 날 null** 이다
     * (0 은 「재봤더니 0명」). 서버에 새로 요청할 것이 없다.
     *
     * 🔴 그리고 **신규 센터 판정의 정본은 온보딩 응답**이다. 그날 브리핑이
     *    있느냐 없느냐로 센터의 단계를 정하면 안 된다 — 그게 이 사고였다.
     */
    const briefingNotGenerated = activeMembers == null;
    const onboardingFinished = onboarding ? resolveOnboarding(onboarding).finished : null;

    const allSetupTasks = useMemo(() => {
        /*
         * 문서가 아직 없는 시간대에는, **세팅이 안 끝난 것이 확인된 센터에만**
         * 세팅 카드를 세운다. 세팅 과제는 그날 배치와 무관하게 사실이라
         * 신규 센터에는 그대로 보여야 하고, 정착 센터에는 나오면 안 된다.
         *
         * ⚠️ 온보딩을 못 읽었으면(null) **안 세우는 쪽**으로 간다. 모르는 채로
         *    세우면 정착 센터가 아침마다 신규 화면을 보는 쪽으로 틀린다 —
         *    반대 방향의 실수보다 훨씬 눈에 띄고 신뢰를 깎는다.
         */
        if (briefingNotGenerated && onboardingFinished !== false) return [];
        return resolveSetupTasks(onboarding, coachSettling);
    }, [onboarding, coachSettling, briefingNotGenerated, onboardingFinished]);
    /*
     * 가린 과제는 목록에서 빠지고, **다음 순위 축이 그 자리로 올라온다** —
     * 회원 카드의 가리기(DISMISSED → 리필)와 같은 관례다.
     * 과도기 센터에서는 상한(2장) 때문에 실제로 승계가 눈에 보이고,
     * 신규 센터에서는 전부 세우므로 그냥 한 장이 사라진다.
     */
    const setupTasks = useMemo(
        () => allSetupTasks.filter((t) => !hiddenSetup.has(t.id)),
        [allSetupTasks, hiddenSetup]
    );

    const handleToggleSetupCheck = (id: string) => setCheckedSetup(toggleCheckedSetupTask(id));
    const handleHideSetup = (id: string) => {
        setHiddenSetup(hideSetupTask(id));
        showToast({
            text: `가렸습니다 · ${SETUP_HIDE_DAYS}일간 다시 알리지 않습니다`,
            undo: {
                label: '되돌리기',
                run: () => setHiddenSetup(unhideSetupTask(id)),
            },
        });
    };
    const composed = useMemo(
        () => composeBriefing(items, coachInsight, BRIEFING_FOCUS_COUNT, setupTasks),
        [items, coachInsight, setupTasks]
    );
    /*
     * ═══════════════════════════════════════════════════════════
     * 과도기 — 회원 카드가 주인공, **미완 강사 카드만 위에 얹는다**
     * ═══════════════════════════════════════════════════════════
     * (2026-09-01 대표 시안 · 정책 §1 ②)
     *
     * 세팅은 끝났는데 아직 정착 전인 센터다. 여기서는 회원 신호가 이미
     * 나오므로 **다섯 건이 본체**이고, 세팅이 덜 된 강사만 카드로 섞인다.
     *
     * 🔴 **자리 규칙은 기존 것을 그대로 계승한다** — 회원이 못 채운 자리만큼,
     *    최대 두 장(`composed.setupTasks` 와 같은 상한). 바뀐 것은 카드의
     *    생김새뿐이다. 상한을 풀면 회원 브리핑이 강사 카드에 밀린다.
     *
     * 폴백: `coachOnboarding` 이 null 이면 빈 배열이라, 아래 렌더가 예전
     * 세팅 축 카드로 그대로 떨어진다.
     */
    const transitionCoachCards = useMemo(() => {
        if (!coachOnboarding) return [];
        /*
         * ─────────────────────────────────────────────────────────────
         * 🔴 자리는 **회원이 못 채운 만큼**이다 (2026-09-02 QA36)
         * ─────────────────────────────────────────────────────────────
         * 예전 식은 `composed.setupTasks.length > 0 ? … : 2` 였다. 그런데
         * 세팅을 졸업한 센터는 `resolveSetupTasks` 가 빈 배열을 주므로
         * **항상 폴백 2가 걸렸다** — 회원 카드가 다섯 건 꽉 찬 정착 센터에도
         * 강사 카드 두 장이 얹혔다. QA11 이 없앤 바로 그 모양이다.
         *
         * 이제 자리 수를 `composeBriefing` 이 계산한 값에서 **읽는다.**
         *   회원 5건 → openSlots 0 → 0장
         *   회원 3건 → openSlots 2 → 2장
         * 「세팅 졸업 여부」는 더 이상 보지 않는다. 졸업했든 아니든 회원
         * 카드가 본체이고, 강사 카드는 남는 자리를 채울 뿐이다.
         */
        const limit = Math.min(composed.openSlots, MAX_SETUP_SLOTS);
        if (limit === 0) return [];
        const states = withOptimistic(
            resolveCoachActions(coachOnboarding.actions, date).states,
            pendingCoachActions,
            date
        );
        /* 가린 강사는 명단에서, 오래된 미수락은 `chore` 에서 빠진다 (QA38) */
        return sortCards(onboardingCoaches.map((c) => toCardView(c, date)))
            .filter((v) => v.chore && !actionStateOf(states, v.row.coachId).hiddenToday)
            .slice(0, limit);
    }, [coachOnboarding, onboardingCoaches, date, composed.openSlots, pendingCoachActions]);

    /**
     * 회원 카드 **위에 실제로 서는** 카드 수 — 번호의 출발점.
     *
     * 🔴 렌더는 `transitionCoachCards.length > 0 ? 강사 카드 : 세팅 축 카드` 로
     *    **둘 중 하나만** 그린다. 그런데 번호는 `composed.setupTasks.length` 만
     *    보고 있어서, 강사 카드가 이긴 날 회원 번호가 강사 카드 장수만큼
     *    당겨졌다(위에 2장이 서 있는데 회원이 1번부터 시작했다).
     *    「위에서부터 세는」 원장님 눈에는 그냥 고장이다.
     *
     * ⚠️ 표시 번호일 뿐이다 — 완료 기록이 쓰는 서버 배열 위치와 무관하다.
     */
    const leadCardCount =
        transitionCoachCards.length > 0 ? transitionCoachCards.length : composed.setupTasks.length;

    /**
     * 회원 줄 버튼 — **실재하는 화면으로만 보낸다** (`coachOnboardingNav`).
     *
     * 갈 데가 없는 것(앱 초대·일지 요청)은 카드가 애초에 버튼으로 안 그리므로
     * 여기 오지 않는다. 그래도 `null` 이면 아무것도 안 한다 — 눌러서 아무 일이
     * 없는 것이, 엉뚱한 화면으로 보내는 것보다 낫다.
     */
    const openMemberAction = (need: MemberNeed, target: MemberActionTarget) => {
        const nav = memberNav(need, target);
        if (!nav) return;
        navigate(nav.to, { state: nav.state });
    };

    const restCount = composed.rest.length;
    /**
     * 🔴 진행률의 분모 — **오늘 챙길 일 다섯 건** 그대로.
     *
     * 합성 카드(세팅 과제·강사 인사이트)가 focus 밖으로 나가면서 focus 는
     * 전부 서버 회원 항목이 됐다. 골라낼 것이 없으니 그대로 센다.
     */
    const focusItems = composed.focus;

    /**
     * 카드 왼쪽에 찍는 순번 — **화면에서 몇 번째인가**.
     *
     * ⚠️ 서버 배열 위치(index)를 그대로 찍으면 안 됩니다. 목록은 위험 순으로
     *    다시 세우므로 1·4·2·7 처럼 뛰는 숫자가 찍히고, "급한 순으로 5건" 이라고
     *    적어 둔 화면에서 그건 고장으로 읽힙니다.
     *    완료 기록에 쓰는 서버 위치는 여기와 **무관합니다** —
     *    handleAction·handleMarkDone·handleUndo 가 정렬 전 items 에서 직접 찾습니다.
     */
    const displayOrder = useMemo(() => {
        const order = new Map<string, number>();
        /*
         * 🔴 **화면에 그려지는 순서대로 1부터** (2026-09-01 QA29 후속).
         *
         * 세팅 과제 카드가 회원 카드 **위**에 선다. 그런데 번호를 「회원 먼저,
         * 과제는 그다음」으로 매겼더니 실화면에 **3·4·1·2** 로 찍혔다 —
         * 위에서부터 세는 원장님 눈에는 그냥 고장이다.
         *
         * 그래서 과제가 가져간 자리만큼 회원 번호를 밀어 둔다.
         * 위에서 아래로 1·2·3·4 가 되고, 「오늘 챙길 일 N건」과도 맞는다.
         *
         * ⚠️ 이건 **표시 번호일 뿐**이다. 완료 기록이 쓰는 서버 배열 위치와는
         *    아무 관계가 없다 — `handleAction`·`handleMarkDone`·`handleUndo` 는
         *    정렬 전 `items` 에서 itemId 로 직접 찾는다. 여기 숫자를 바꿔도
         *    엉뚱한 항목이 완료되지 않는다.
         */
        let n = leadCardCount;
        composed.focus.forEach((slot) => {
            if (slot.kind === 'ITEM') order.set(slot.item.itemId, n);
            /* 강사 카드도 한 자리를 차지한다 — 건너뛰면 아래 번호가 하나씩 당겨진다 */
            n += 1;
        });
        composed.rest.forEach((slot) => {
            order.set(slot.item.itemId, n);
            n += 1;
        });
        return order;
    }, [composed, leadCardCount]);
    const orderOf = (itemId: string) => displayOrder.get(itemId) ?? 0;

    /** 상세를 열어 둔 항목 — 항상 **최신 items** 에서 찾는다 (복사본을 들고 있지 않는다) */
    const openedItem = openItemId ? (items.find((i) => i.itemId === openItemId) ?? null) : null;
    /**
     * 강사 카드를 눌러 연 **데이터 드로어**.
     *
     * 예전에는 카드를 누르면 곧바로 PDF 리포트(CoachReportSheet)가 열렸습니다.
     * 대표 요청으로 데이터 팝업이 먼저 오고, 리포트는 그 안의 버튼으로 갑니다.
     * 분석 결과를 통째로 넘깁니다 — 드로어가 다시 계산하면 리포트와 숫자가 갈립니다.
     */
    const openedCoachAnalysis = openCoachId
        ? (coachAnalyses.find((a) => a.coach.coachId === openCoachId) ?? null)
        : null;
    /** 데이터 드로어에서 '월간 리포트(PDF)' 를 눌러 연 리포트 */
    const [reportCoachId, setReportCoachId] = useState<string | null>(null);
    const reportCoach = reportCoachId
        ? (coachAnalyses.find((a) => a.coach.coachId === reportCoachId)?.coach ?? null)
        : null;

    /** 서버 왕복 중이면 목록 전체를 잠근다 — 연타로 같은 지시가 두 건 생긴다 */
    const rowsLocked = confirm.sending || setItemDone.isLoading || setItemStatus.isLoading;

    const showToast = (next: Toast, ms = 2600) => {
        setToast(next);
        window.setTimeout(() => setToast((cur) => (cur === next ? null : cur)), ms);
    };
    const showMessage = (msg: string) => showToast({ text: msg });

    /** 며칠 전 브리핑을 뒤늦게 처리하는 경우가 흔하다. 언제 기록인지 같이 알려준다 */
    const withDayNote = (msg: string) => {
        const daysAgo = dayjs().startOf('day').diff(date, 'day');
        return daysAgo > 0 ? `${msg} · ${daysAgo}일 전 브리핑 건입니다` : msg;
    };

    /** 확인 창과 완료 알림에 같이 붙는 날짜 맥락. 오늘 것이면 붙일 말이 없다 */
    const dayNote = (): string | undefined => {
        const daysAgo = dayjs().startOf('day').diff(date, 'day');
        return daysAgo > 0 ? `${daysAgo}일 전 브리핑 건입니다` : undefined;
    };

    const failMessage = (error: unknown) => {
        const msg = (error as { response?: { data?: { message?: string } } } | null)?.response?.data
            ?.message;
        return msg || '처리하지 못했습니다. 잠시 후 다시 시도해 주세요';
    };

    /**
     * 추천 행동 실행.
     *
     * draft 는 카드에서 고친 문구입니다 — "고친 내용 그대로 전달됩니다"라고
     * 써 두고 원본을 보내면 그것도 거짓말입니다.
     */
    const handleAction = (itemId: string, actionCode: string, draft: string) => {
        /*
         * 연타 방지. 지시 생성은 멱등이 아니라서 두 번 누르면 같은 지시가 두 건 생긴다.
         * 강사 화면에 같은 회원이 두 줄로 뜨면 "이거 이미 한 건데?" 가 된다.
         */
        if (confirm.sending || setItemDone.isLoading || gift.sending) return;

        const index = items.findIndex((i) => i.itemId === itemId);
        const item = items[index];
        if (!item) return;

        /*
         * ⚠️ 선물은 **가장 먼저** 갈라낸다. 여기서 return 하지 않으면
         *    아래 canCreateOrder 를 지나 setItemDone 까지 떨어진다 —
         *    보내지도 않은 선물이 "완료"로 기록되고, 담당 강사가 붙어 있으면
         *    강사에게 없던 지시가 나간다. 둘 다 되돌릴 수 없다.
         *    (briefingGiftSend.test.tsx 가 이 순서를 잠그고 있다)
         */
        if (actionCode === GIFT_ACTION_CODE) {
            if (item.subjectType !== 'MEMBER' || !item.subjectId) {
                showMessage('선물은 회원 항목에서만 보낼 수 있습니다');
                return;
            }
            gift.open({
                memberId: item.subjectId,
                memberName: item.subjectName,
                summary: item.title,
                dateKey,
            });
            return;
        }

        // 추천 액션이면 추천 라벨을, 대안 액션이면 그 라벨을 쓴다
        const label =
            item.recommendation.actionCode === actionCode
                ? item.recommendation.actionLabel
                : (item.actions.find((a) => a.code === actionCode)?.label ?? '처리');

        /*
         * 지시가 되는 건은 **바로 보내지 않고 확인 창을 엽니다.**
         * 누르는 즉시 나가면 어떤 문장이 강사에게 가는지 볼 기회가 없고,
         * 잘못 눌러도 되돌릴 방법이 없습니다. 실제 전송은 confirm 안에서만 일어납니다.
         */
        if (canCreateOrder(item, actionCode)) {
            confirm.ask({
                memberId: item.subjectId,
                memberName: item.subjectName,
                coachId: item.relatedCoachId,
                coachName: item.relatedCoachName,
                // 화면이 보여준 판단 문장 그대로. 요약하지 않는다.
                task: item.title,
                /*
                 * 카드의 초안은 **회원에게 보낼 글**이다 ("○○ 회원님, …").
                 * 그래서 memberDraft 로 간다. 확인 창의 기본값이 되고 거기서 한 번 더 고칠 수 있다.
                 *
                 * ⚠️ 예전에는 이 값을 ownerNote 로 보냈다. ownerNote 는 원장이 **강사에게**
                 *    하는 말이라, 강사 앱이 회원용 인사말을 '원장님 메모'로 띄웠다.
                 *    되돌리지 말 것 — orderConfirmFlow.test.tsx 가 잠그고 있다.
                 */
                memberDraft: draft,
                /*
                 * 아래 셋은 **요청에 실리지 않는다** — 확인 창의 강사 화면
                 * 미리보기를 실제와 같게 그리기 위한 값이다.
                 *
                 *  · sourceDraft 원장이 초안을 통째로 지우고 보내면 요청에서 키가 빠지고,
                 *                서버가 이 항목의 초안으로 도로 채운다
                 *                (opsBriefing.service.resolveMemberDraft). 미리보기는
                 *                그 사실을 알아야 "앱이 첫 마디를 만든다"고 잘못 말하지 않는다.
                 *  · reason/facts 서버가 이 항목에서 그대로 굳혀 강사 앱에 실어 보낸다
                 *                (loadSourceSnapshot → reasonSnapshot·factsSnapshot).
                 *                즉 강사 카드에 뜨는 근거는 지금 이 화면이 보여준 그 값이다.
                 */
                sourceDraft: item.recommendation.draft,
                reason: item.recommendation.reason,
                facts: item.facts,
                // 강사에게 덧붙일 말은 원장이 확인 창에서 직접 적는다. 화면이 채워 두지 않는다.
                ownerNote: '',
                ...orderValueOf(item),
                sourceItemId: item.itemId,
                sourceDate: dateKey,
                contextNote: dayNote(),
            });
            return;
        }

        setItemDone.mutate(
            { index, itemId, done: true },
            {
                onSuccess: () => showMessage(withDayNote(`${label} 완료로 기록했습니다`)),
                onError: (e) => showMessage(failMessage(e)),
            }
        );
    };

    /**
     * 체크박스 — 완료로만 기록한다.
     *
     * ⚠️ handleAction 을 여기에 재사용하면 안 됩니다.
     * 예전에는 카드 왼쪽 네모(title="완료로 표시")가 주 CTA 와 같은 handleAction 을
     * 불렀습니다. 원장님이 "이건 내가 이미 전화했다"는 뜻으로 네모를 누르면
     * 그때마다 강사 앞으로 지시가 새로 만들어져 진행 현황에 쌓였습니다 —
     * 이미 끝난 일을 강사에게 시키는 지시였습니다.
     *
     * 지시를 만드는 경로는 주 CTA(handleAction) 하나뿐입니다.
     */
    const handleMarkDone = (itemId: string) => {
        if (confirm.sending || setItemDone.isLoading) return;
        const index = items.findIndex((i) => i.itemId === itemId);
        if (index < 0) return;
        setItemDone.mutate(
            { index, itemId, done: true },
            {
                onSuccess: () => showMessage(withDayNote('완료로 기록했습니다')),
                onError: (e) => showMessage(failMessage(e)),
            }
        );
    };

    const handleUndo = (itemId: string) => {
        const index = items.findIndex((i) => i.itemId === itemId);
        if (index < 0) return;
        setItemDone.mutate(
            { index, itemId, done: false },
            {
                onSuccess: () => showMessage('완료 처리를 취소했습니다'),
                onError: (e) => showMessage(failMessage(e)),
            }
        );
    };

    /** 가린 항목을 다시 목록으로 (서버에도 PENDING 으로 남는다) */
    const handleRestore = (itemId: string) => {
        setItemStatus.mutate(
            { itemId, status: 'PENDING' },
            {
                onSuccess: () => showMessage('다시 목록에 넣었습니다'),
                onError: (e) => showMessage(failMessage(e)),
            }
        );
    };

    /**
     * 가리기 — "이 데이터는 안 보이게 해 주세요".
     *
     * ─────────────────────────────────────────────────────────────
     * 백필은 여기서 하지 않습니다.
     * ─────────────────────────────────────────────────────────────
     * 훅이 항목 상태를 DISMISSED 로 바꾸면 편성 함수가 그 항목을 미처리 목록에서
     * 빼고 **다음 위험 순위를 그 자리에 올립니다.** 화면이 따로 한 칸을 당겨오면
     * 새로고침 뒤 서버 상태와 어긋나는 순간 목록이 조용히 달라집니다.
     *
     * ⚠️ 실패하면 훅이 되돌립니다. 여기서는 실패를 **말하는** 것만 합니다 —
     *    "가렸습니다" 라고 해 놓고 새로고침에 되돌아오는 것이 가장 나쁩니다.
     */
    const handleHide = (itemId: string) => {
        if (setItemStatus.isLoading) return;
        const target = items.find((i) => i.itemId === itemId);
        if (!target) return;
        /*
         * 지시가 나간 항목은 애초에 버튼을 안 그립니다(BriefingCard 의 ACTED 접힘 카드).
         * 여기서 한 번 더 막는 것은 서버 409 를 만들지 않기 위해서입니다 —
         * 목록이 갱신되기 전 찰나에 상태가 ACTED 로 바뀌어 있을 수 있습니다.
         */
        if (target.status === 'ACTED') {
            showMessage('지시를 낸 항목은 가릴 수 없습니다');
            return;
        }
        /* 상세를 열어 둔 채 가리면 빈 드로어가 남는다 */
        if (openItemId === itemId) setOpenItemId(null);

        setItemStatus.mutate(
            { itemId, status: 'DISMISSED' },
            {
                onSuccess: () =>
                    showToast(
                        {
                            text: `${target.subjectName} 건을 가렸습니다`,
                            undo: { label: '되돌리기', run: () => handleRestore(itemId) },
                        },
                        /* 되돌리기를 누를 시간을 준다. 2.6초는 문장을 읽으면 끝난다 */
                        6000
                    ),
                onError: (e) => showMessage(failMessage(e)),
            }
        );
    };

    const pending = items.filter((i) => i.status === 'PENDING');
    /**
     * 가린 건은 진행률의 **분모에서도 뺍니다.**
     *
     * 예전에는 `items.length - pending.length` 를 처리 건수로 썼는데,
     * 가리기가 생기면서 그 식이 가린 건까지 "처리했다"로 셉니다.
     * 가린 것은 처리한 것이 아니라 **안 하기로 한 것**입니다 —
     * 둘을 같은 숫자에 넣으면 "3/8건 처리" 가 무슨 뜻인지 알 수 없게 됩니다.
     */
    const dismissedCount = composed.dismissed.length;
    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 진행률은 **오늘 세운 것**을 분모로 쓴다 (2026-08-28 대표 결정)
     * ─────────────────────────────────────────────────────────────
     * 예전 분모는 `items.length - dismissedCount` — 그날 올라온 **전부**였다.
     * 그래서 머리말은 「챙길 일 5건 · 외 5건」인데 진행률만 「전체 9건」이라,
     * 같은 화면의 두 숫자가 서로 다른 모집단을 세고 있었다. 원장님은 5건을
     * 다 처리하고도 「5/9」를 보고 아직 절반도 안 했다고 읽는다.
     *
     * 「외 N건」은 **오늘 할 일이 아니라 추가 브리핑**이다. 내일도 거기 있고,
     * 오늘 안 해도 되는 것을 분모에 넣으면 매일 미달로 끝난다.
     *
     * 그래서 분모는 focus 중 처리 가능한 것(`focusItems`)이고, 분자는 그중
     * 처리한 것이다. 둘이 같은 배열에서 나오므로 어긋날 수가 없다.
     */
    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 세팅 과제도 **진행률에 센다** (2026-09-01 QA29, 대표 지시)
     * ─────────────────────────────────────────────────────────────
     * 예전에는 합성 카드를 분모에서 뺐다. 회원 카드가 본체이던 시절의 규칙이다.
     * 그런데 **신규 센터는 회원 카드가 0건**이라 분모도 0 이 되고, 진행률이
     * 「0 / 0건」으로 영원히 의미를 못 가졌다 — 그 센터가 그날 한 일이 화면에
     * 하나도 안 남는다.
     *
     * 화면에 세운 카드가 곧 오늘 할 일이므로, 세운 것을 그대로 센다.
     * (가린 과제는 `setupTasks` 단계에서 이미 빠졌다 — 「안 하기로 한 것」은
     *  회원 카드와 마찬가지로 분모에 안 들어간다)
     */
    const setupShown = composed.setupTasks;
    const setupDone = setupShown.filter((t) => checkedSetup.has(t.id)).length;
    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 분모는 **화면에 실제로 서는 카드**다 (2026-09-02 QA37)
     * ═══════════════════════════════════════════════════════════
     * `composed.setupTasks` 를 그대로 셌다. 그런데 목록은
     * `transitionCoachCards.length > 0 ? 강사 카드 : 세팅 축 카드` 로
     * **둘 중 하나만** 그린다. 강사 카드가 이긴 날에는 세우지도 않은
     * 축 카드를 세고 있었던 셈이다.
     *
     * 과도기에서는 두 목록이 같은 상한(`min(openSlots, 2)`)을 쓰기 때문에
     * 수가 우연히 맞을 때가 많았지만, 챙길 강사가 한 명뿐인 날처럼
     * **강사 카드가 상한보다 적으면** 분모만 부풀었다.
     *
     * `leadCardCount` 는 번호 매기기가 이미 쓰는 값이다 — 번호와 진행률이
     * 같은 수를 보게 된다.
     */
    const resolvedCoachActions = resolveCoachActions(coachOnboarding?.actions, date);
    /* 저장이 끝난 시점의 서버 모양을 읽기 위한 최신값 — 클로저에 갇힌 클릭 시점 값이 아니다 */
    serverBackedRef.current = resolvedCoachActions.serverBacked;
    const coachActionStates = withOptimistic(
        resolvedCoachActions.states,
        pendingCoachActions,
        date
    );
    /*
     * 과도기 강사 카드의 체크·가리기 — 신규 센터 화면의 `act` 와 같은 순서.
     *
     * 임시 값(pending)의 수명:
     *   · 저장 성공 → 서버 이력을 **다시 읽은 뒤** 거둔다. 그때부터 표시는 정본(서버)에서 온다.
     *     서버가 그 표시를 안 주면(막힌 단계가 바뀜·만료) 그대로 걷힌다 — 서버의 규칙이다.
     *     서버 이력을 못 읽었으면 남겨 둔다. 저장은 됐으니 걷는 쪽이 더 틀리다.
     *   · 저장 실패 → 바로 거둔다(눌린 티를 남기면 저장된 줄 안다). 구서버면 방금 남긴
     *     로컬 기록이 대신 보인다.
     * 거두지 않으면 임시 값이 세션 내내 서버를 덮어, 단계가 바뀌어도 화면이 안 바뀐다.
     */
    const actOnCoach = async (coachId: string, type: CoachActionType, name: string) => {
        const key = `${coachId}|${type}`;
        if (inFlightCoachActions.current.has(key)) return;
        inFlightCoachActions.current.add(key);
        setPendingCoachActions((p) => [...p, { coachId, type }]);
        const dropPending = () =>
            setPendingCoachActions((p) => p.filter((x) => !(x.coachId === coachId && x.type === type)));
        try {
            const { saved, note } = await persistCoachAction({
                coachId,
                type,
                today: date,
                serverBacked: () => serverBackedRef.current,
                persist: (id, t) => postOpsBriefingActionAPI(ctx, { coachId: id, type: t }),
            });
            if (saved) {
                try {
                    await refreshCoachOnboarding();
                    dropPending();
                } catch {
                    /* 서버 이력을 못 읽음 — 임시 값을 남겨 둔다 */
                }
            } else {
                dropPending();
            }
            showToast({ text: coachActionToast(name, type, note) });
        } finally {
            inFlightCoachActions.current.delete(key);
        }
    };
    /**
     * 앞줄 카드 중 **처리한 것**.
     *
     * 🔴 분모에 넣었으면 **셀 수 있어야 한다.** 이 화면은 「체크할 수 없는
     *    강사 인사이트는 분모에서 뺀다 — 넣으면 100% 에 영영 못 닿는다」는
     *    규칙을 이미 갖고 있다. 강사 온보딩 카드는 체크할 수 있으므로
     *    분모에 넣고, 그 대신 **체크한 것을 분자에도 넣는다.**
     *    (강사 카드의 「연락함」은 3일짜리 조용한 모드다 — 카드가 스스로
     *     그렇게 표시하므로 진행률도 같은 것을 처리로 본다)
     */
    const leadDone =
        transitionCoachCards.length > 0
            ? transitionCoachCards.filter((v) => actionStateOf(coachActionStates, v.row.coachId).quiet)
                  .length
            : setupDone;
    const actedInFocus =
        focusItems.filter((s) => s.item.status === 'ACTED').length + leadDone;
    const trackedCount = focusItems.length + leadCardCount;
    /**
     * 아직 남은 건 — **분모에서 처리한 것을 뺀 값**이다.
     *
     * 따로 세지 않는다. 같은 배열에서 나온 두 수를 빼야
     * 「분모 = 처리 + 남음」이 깨질 수가 없다.
     */
    const remainingCount = Math.max(0, trackedCount - actedInFocus);
    /** 세팅 과제만 있는 날, 그것까지 다 끝냈는가 */
    const setupAllDone = focusItems.length === 0 && setupShown.length > 0 && setupDone === setupShown.length;
    /*
     * 아래 사실 줄(미처리·위험·회원·강사)도 **같은 모집단**으로 옮긴다.
     *
     * 예전에는 pending(그날 전부) 기준이라 머리말과 다른 축이었다. 「머리말은
     * 지금 볼 것, 이 줄은 오늘 전부」로 나눠 뒀는데, 실제 화면에서는 두 숫자가
     * 나란히 있어서 원장님이 같은 것의 내역으로 읽는다 — 그게 이번 버그의
     * 절반이다. 이제 이 줄은 **머리말 다섯 건의 내역**이다.
     *
     * 전체 건수를 버리지는 않는다. 「외 N건」이 머리말에 그대로 남아 있어
     * 오늘 올라온 총량은 5 + restCount 로 읽힌다.
     */
    const focusPending = focusItems.filter((s) => s.item.status === 'PENDING');
    const urgentCount = focusPending.filter((s) => s.item.grade === 'DANGER').length;
    const memberCount = focusPending.filter((s) => s.item.subjectType === 'MEMBER').length;
    const coachCount = focusPending.filter((s) => s.item.subjectType === 'COACH').length;
    /*
     * ⚠️ **편성 안내(explainQuota·explainComposition)는 날짜 전체를 써야 한다.**
     *
     * 그 문구가 말하는 층은 「서버가 그날 무엇을 브리핑에 올렸나」이지
     * 「화면이 무엇을 세웠나」가 아니다 (briefingPolicy 의 "편성과 표시는 다른 층"
     * 주석). 여기에 focus 기준 수(항상 ≤5)를 넣으면 `danger > maxItems` 가
     * 영원히 거짓이 되어 「위험 N건은 상한과 무관하게 모두 올렸습니다」가
     * 통째로 사라진다 — 상한에 걸린 날을 원장이 알 수 없게 된다.
     */
    const dangerAll = pending.filter((i) => i.grade === 'DANGER').length;


    /*
     * 재무 가치 합계 — 정책 M2 · M4 (2026-08-31 대표 확정본).
     *
     * 💰 지켜야 할 매출  = Σ 미사용 선수금 (전 항목)
     * 📈 확보 가능한 매출 = Σ 재등록 기대 (회원당 1회)
     *
     * 🔴 두 숫자는 **더할 수 없습니다.** 앞은 이미 받은 돈, 뒤는 확률을 곱한
     *    기댓값입니다. 예전에는 한 항목이 등급에 따라 둘 중 한쪽 통에만 들어가서
     *    「재등록 기회」 카드의 선수금이 어느 합계에도 안 잡혔습니다.
     *
     * 금액을 모르는 건(무료권·강사 항목)은 value 가 null 이다.
     * 0으로 세면 합계는 같아 보이지만 "챙길 게 없는 건"으로 묻힌다 —
     * 아래 `unpricedCount` 가 그 수를 따로 밝힌다.
     */
    const totals = summarizeValue(pending);
    const prepaidAmount = totals.prepaid;
    const expectedAmount = totals.expected;
    /**
     * 금액을 못 구한 건 수 — 합계 옆에 밝힌다.
     *
     * 🔴 **회원 항목만 센다.** `countUnpriced` 는 `value == null` 인 항목을 전부
     * 세는데, 강사·센터 항목은 **원래 금액이 없는** 항목입니다. 그걸 「못 낸 건」에
     * 넣으면 "이만큼은 금액을 못 냈습니다"가 부풀려지고, 원장님은 합계가 실제보다
     * 더 많이 빠진 것으로 읽습니다.
     *
     * 전에는 목이 강사 항목에도 금액을 적어 둬서 이 차이가 안 보였습니다 —
     * 실서버는 강사 항목의 value 를 null 로 보내므로 운영에서는 늘 과다 계상이었습니다.
     */
    const unpricedCount = totals.unpriced;
    /**
     * 헤더 두 금액이 **무엇을 세고 있는지** — 범위를 숫자로 못 박는다.
     *
     * 🔴 회원 현황 헤더와 **같은 이름의 숫자인데 모집단이 다르다.**
     *    거기는 센터 전 회원(실측 2.7억대), 여기는 그날 올라온 카드(실측 수백만).
     *    범위를 안 적으면 원장님은 두 자릿수 배 차이를 보고 둘 중 하나를
     *    고장으로 읽는다. (2026-08-31 대표 확정 ⓐ — 브리핑 헤더는 그날 몫 유지)
     *
     * N 은 **회원 카드 수**다. 강사·센터 항목은 애초에 금액이 없으므로
     * 분모에서 뺀다 — `summarizeValue` 가 그 규칙으로 이미 갈라 놨다.
     */
    const scopedCards = totals.priced + totals.unpriced;
    const scopeNote = `${dayWord} 올라온 ${scopedCards}건 기준`;

    // 브리핑 편성 — 센터 규모에 따라 건수가 달라진다. 활성 회원 수를 모르면 규모도 모른다.
    const centerSize = resolveCenterSize(activeMembers);
    const quota = centerSize ? QUOTA[centerSize] : null;
    /*
     * 🔴 `quotaNoteOf` 는 문장과 함께 **그 문장의 성격**을 준다 (2026-09-01 QA32).
     *    편성만 말하는 갈래는 이 화면이 안 그린다 — 아래 렌더 주석 참고.
     *    문자열로 판단하지 않는 이유도 거기 적혀 있다.
     */
    const quotaNote = quotaNoteOf({
        size: centerSize,
        total: pending.length,
        danger: dangerAll,
        activeMembers,
        /* 상한·보장은 서버가 그날 실제로 적용한 값을 쓴다. 프론트 상수로 다시 계산하지 않는다 */
        composition,
    });
    const won = (n: number) => n.toLocaleString('ko-KR');

    const today = date;
    const weekday = ['일', '월', '화', '수', '목', '금', '토'][today.day()];

    /**
     * 세울 것이 하나도 없을 때만 쓰는 문장.
     *
     * 항목이 남아 있는 날의 머리말은 아래 헤더에서 **편성 프레임**으로 만듭니다
     * (「오늘 챙길 일 5건 · 외 26건」). 이 문장은 전부 가렸거나 다 처리해서
     * 세울 카드가 없는 날에만 나옵니다.
     */
    const summaryLine =
        pending.length > 0
            ? `${dayWord} 확인하실 일이 ${pending.length}건 있습니다`
            : /*
               * 세팅 과제가 있으면 「없습니다」라고 하지 않는다 — 아래 빈 상태와
               * 같은 모순이다 (2026-09-01 QA24). 헤더와 본문이 다른 말을 하면
               * 원장님은 화면 전체를 못 믿는다.
               */
              setupTasks.length > 0
              ? `${dayWord} 챙길 센터 설정이 ${setupTasks.length}건 있습니다`
              : `${dayWord}은 급하게 챙길 일이 없습니다`;

    /*
     * 헤더 밑줄에 적을 사실들.
     *
     * ⚠️ 0 인 조각은 넣지 않는다. "회원 0명 · 강사 0명 · 어제 지시 0건 중 0건 완료" 는
     * 매일 같은 자리에서 아무 말도 하지 않으면서 눈만 가져간다. 이 화면의 다른 자리는
     * 이미 그렇게 하고 있다(편성 안내는 빠진 게 없으면 안 뜬다) — 그 관용구를 여기에도 쓴다.
     *
     * ⚠️ 다만 **모르는 값을 0 으로 접지는 않는다.** 어제 지시 요약은 서버가 항상 숫자로
     * 주는 값이라(yesterdayOrders) 0 이면 "어제 지시가 없었다"는 사실이다.
     */
    /*
     * ⚠️ 머리말이 「챙길 일 5건」으로 바뀌면서 이 줄이 **분모를 밝히는 자리**가 됐다.
     *
     * 예전 머리말은 전체 위험 수(28)를 외쳤고 이 줄은 그 내역이었다.
     * 이제 머리말은 화면에 세운 다섯 건을 말하므로, 전체가 몇 건이었는지는
     * 여기 말고는 남을 자리가 없다. 「미처리 N건 · 위험 N건」을 앞에 세워
     * 뒤따르는 회원·강사 수가 무엇의 내역인지 읽히게 한다.
     *
     * 이 숫자들은 전부 **미처리(pending) 기준**이다 — 머리말의 다섯 건과
     * 다른 축이라 섞이지 않는다. 머리말은 "지금 볼 것", 이 줄은 "오늘 전부".
     */
    const headerFacts = [
        /*
         * 🔴 「미처리」도 **머리말·진행률과 같은 모집단**이다 (2026-09-02 QA37).
         *    `pendingInFocus` 는 회원 카드만 세는 값이라, 앞줄 카드가 선 날
         *    머리말 5건 · 미처리 3건 처럼 갈렸다. `remainingCount` 는
         *    「분모 − 처리」라 정의상 어긋날 수가 없다.
         *    (아래 회원·강사 수는 성격별 내역이라 그대로 둔다)
         */
        remainingCount > 0 ? `미처리 ${remainingCount}건` : null,
        urgentCount > 0 ? `위험 ${urgentCount}건` : null,
        memberCount > 0 ? `회원 ${memberCount}명` : null,
        coachCount > 0 ? `강사 ${coachCount}명` : null,
        yesterday.total > 0
            ? `어제 지시 ${yesterday.total}건 중 ${yesterday.done}건 완료`
            : null,
    ].filter((v): v is string => v != null);

    /*
     * 밀린 지시가 있는가 — **진행 현황을 어디에 놓을지**를 이걸로 정한다.
     *
     * 어제 이전에 낸 지시가 아직 안 끝났다면 그건 오늘 새로 올라온 일보다 먼저다.
     * 이미 늦은 것이고, 원장님이 지금 당장 움직여야 하는 유일한 종류다.
     * 진행 현황이 항상 맨 아래에 있으면 스크롤을 안 내린 날에는 없는 것과 같다.
     *
     * ⚠️ 두 벌로 그리지 않는다. 같은 OrderTracker 하나를 위나 아래 중 한 곳에만 놓는다 —
     *    같은 사실을 두 군데서 말하면 어느 쪽도 안 읽힌다.
     * ⚠️ 이 판정은 **보여줄 자리만** 정한다. 숫자·분모는 OrderTracker 가 자기 쿼리로 센다.
     */
    const hasBacklog = toOrderViews(orders ?? []).some(
        (o) =>
            o.status !== 'DONE' &&
            o.status !== 'ARCHIVED' &&
            o.status !== 'RETRACTED' &&
            o.sentDayOffset > 0
    );

    /**
     * 지시 및 진행 현황 한 벌.
     * 위에 놓든 아래에 놓든 **이것 하나**만 그린다 (hasBacklog 주석 참고).
     */
    const trackerSection = (
        <div>
            <OrderTracker />
            <div className="flex justify-end" style={{ marginTop: 10 }}>
                <button
                    type="button"
                    onClick={() => setShowCoachApp(true)}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 14,
                        color: '#5B6B7F',
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                        padding: 0,
                    }}
                >
                    강사 앱에서 어떻게 보이는지 확인하기
                </button>
            </div>
        </div>
    );

    /* ── 상태 분기 ─────────────────────────────────────────── */

    if (!ctx.ready) {
        return (
            <OpsError
                title="센터 정보를 확인할 수 없습니다"
                detail="로그인 정보가 만료되었을 수 있습니다"
            />
        );
    }

    if (isLoading) {
        return (
            <div className="flex w-full flex-col gap-[16px] pb-[40px]">
                <DateNavigator value={date} onChange={setDate} label="시그널" />
                <OpsLoading variant="page" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex w-full flex-col gap-[16px] pb-[40px]">
                <DateNavigator value={date} onChange={setDate} label="시그널" />
                <OpsError onRetry={() => refetch()} />
            </div>
        );
    }

    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 신규 센터 — **화면 전체를 강사별 온보딩으로 대체한다**
     * ═══════════════════════════════════════════════════════════
     * (2026-09-01 대표 시안 · 정책 §1)
     *
     * 세팅이 안 끝난 센터에는 분석할 회원 데이터가 없다. 예약도 수강권도
     * 없으니 이탈 위험·재등록 기회가 나올 수가 없고, 기존 브리핑을 띄우면
     * 원장님은 **빈 화면**을 본다. 그 센터에서 그날 할 일은 전부 세팅이다.
     *
     * ⚠️ **`items.length === 0` 분기보다 먼저 와야 한다.** 신규 센터는 브리핑
     *    항목이 0건이라 그 아래 빈 화면으로 떨어진다 — 순서를 바꾸면 이
     *    화면은 영원히 안 뜬다.
     *
     * ⚠️ 히어로의 「N/5단계」는 **서버가 준 세팅 단계**를 그대로 적는다
     *    (`setupSteps`). 프론트에서 다시 세면 화면 안에서 숫자가 갈린다.
     *
     * ═══════════════════════════════════════════════════════════
     * 🔴 그러나 **화면을 대체할지는 세팅 단계로 정하지 않는다** (2026-09-02 QA36)
     * ═══════════════════════════════════════════════════════════
     * 예전 조건은 `setupSteps.done < total` 하나뿐이었다. 그런데 그 값은
     * `resolveOnboarding().finished` 그대로라, **수강권 없는 회원이 한 명이라도
     * 남아 있으면 영원히 미졸업**이다.
     *
     * 실측(2026-09-02, 센터 69cb8857…): 회원 448명 · 강사 19명 · 그날 브리핑
     * **회원 항목 15건**. 그런데 수강권 없는 회원 216명 때문에 미졸업으로 읽혀
     * 매일 아침 **신규 센터 화면**이 떴다. 원장님이 챙겨야 할 위험 회원 15건은
     * 그 뒤에 통째로 가려져 있었다.
     *
     * 판정 기준을 **회원 카드 수**로 옮긴다 — QA11 자리 보충 규칙과 같은 분모다
     * (`composeBriefing` 이 계산해 준 `centerStage`).
     *   회원 카드 0건   → 세울 것이 세팅뿐이다 → 이 화면이 전부를 대체한다
     *   회원 카드 1~4건 → 회원이 본체 → 남는 자리만 강사 카드로 보충
     *   회원 카드 5건   → 정착 → 강사 카드 0장
     *
     * ⚠️ **`briefingNotGenerated` 를 여기에 걸지 마라** (한 번 넣었다가 뺐다).
     *    「생성 전에는 판정 보류」가 맞아 보이지만, 진짜 신규 센터의 브리핑
     *    문서는 `activeMembers: null` 이다(픽스처가 그 상태다 —
     *    `onboardingScreen.test.tsx`, `MorningBriefingGate.test.tsx`).
     *    그 조건을 걸면 이 화면이 **존재 이유인 센터에서 영영 안 뜬다.**
     *    생성 전 시간대 문제는 세팅 카드 쪽에서 따로 막는다(3889b14d).
     *
     * 폴백: `coachOnboarding` 이 null(=404, 배포 전)이면 이 분기는 통째로
     * 거짓이 되어 예전 화면이 그대로 뜬다.
     */
    const setupIncomplete =
        coachOnboarding != null &&
        coachOnboarding.setupSteps.done < coachOnboarding.setupSteps.total;
    if (coachOnboarding && setupIncomplete && composed.centerStage === 'NEW') {
        return (
            <div className="flex w-full flex-col gap-[16px] pb-[40px]">
                <DateNavigator value={date} onChange={setDate} label="시그널" />
                {isOpsBriefingMock() && <OpsMockBadge />}
                {!isToday && (
                    <PastDateBanner date={date} onBackToToday={() => setDate(dayjs().startOf('day'))} />
                )}
                <OnboardingBriefingScreen
                    /* 가린 강사를 뺀 명단으로 그린다 — 카드·요약이 같은 배열에서 나온다 (QA38) */
                    briefing={{ ...coachOnboarding, coaches: onboardingCoaches }}
                    dateLabel={`${date.format('YYYY년 M월 D일 dddd')} · 손실 시그널`}
                    today={date}
                    onToast={(text) => showToast({ text })}
                    onOpenMember={openMemberAction}
                    /*
                     * 서버에 남기기 — 없으면(구서버) false 를 돌려주고, 화면이
                     * 이 브라우저에만 남긴 뒤 그 한계를 문구로 밝힌다.
                     */
                    onPersist={(coachId, type) =>
                        postOpsBriefingActionAPI(ctx, { coachId, type })
                    }
                    onSaved={refreshCoachOnboarding}
                />
                {toast && <BriefingToast toast={toast} onUndo={() => setToast(null)} />}
            </div>
        );
    }

    // 브리핑이 하나도 없는 날은 오류가 아니다. 조용한 날일 뿐이다.
    if (items.length === 0) {
        /*
         * 두 가지를 구분한다.
         *   activeMembers === null → 그날 브리핑이 아직/영영 만들어지지 않았다
         *   activeMembers 가 숫자   → 브리핑은 돌았고, 올릴 항목이 없었다
         * 서버가 활성 회원 수를 브리핑 생성 시점에 굳혀 두기 때문에 이 구분이 가능하다.
         * 둘을 같은 문구로 뭉개면 "우리 센터는 아무 문제 없구나"로 읽힌다.
         */
        const notGenerated = activeMembers == null;
        return (
            <div className="flex w-full flex-col gap-[16px] pb-[40px]">
                <DateNavigator value={date} onChange={setDate} label="시그널" />
                {!isToday && (
                    <PastDateBanner date={date} onBackToToday={() => setDate(dayjs().startOf('day'))} />
                )}
                {/*
                  🔴 신규 센터가 떨어지는 자리가 여기다.
                     브리핑 항목이 0건이라 「오늘은 챙길 일이 없습니다」만 뜨는데,
                     실제로는 **세팅이 안 끝난 것**이다. 빈 문구 위에 할 일을 놓는다.

                     ⚠️ 이 분기에는 아래 카드 목록이 없다. 그래서 합성 과제 카드를
                        **여기서 직접** 그린다 — 안 그리면 신규 센터에는 화면에
                        아무 할 일도 안 남는다(상세 섹션을 뺀 뒤로는 더욱).
                */}
                {/*
                  🔴 진행률을 **이 분기에도** 그린다 (2026-09-01 QA29).
                     신규 센터는 여기로 떨어지는데, 예전에는 이 분기에 진행률이
                     아예 없어서 원장님이 그날 한 일이 화면에 하나도 안 남았다.
                     세팅 과제를 분모에 넣은 것도 같은 이유다.
                */}
                {composed.setupTasks.length > 0 && (
                    <div
                        aria-label="오늘 진행률"
                        className="flex w-full items-baseline rounded-[12px] border border-solid bg-white"
                        style={{ borderColor: '#C9D9EE', padding: '14px 22px', gap: 8 }}
                    >
                        <span style={{ fontSize: 26, fontWeight: 800, color: '#2C4C8C' }}>
                            {setupDone}
                        </span>
                        <span
                            style={{ fontSize: 15, color: '#7C8794' }}
                            title="오늘 세운 세팅 과제만 셉니다. 가린 과제는 빠집니다. 체크는 이 브라우저에서만 기억됩니다."
                        >
                            / 오늘 챙길 일 {composed.setupTasks.length}건 처리
                        </span>
                        {setupDone < composed.setupTasks.length && (
                            <span style={{ fontSize: 14.5, color: '#C22D38', marginLeft: 6 }}>
                                아직 {composed.setupTasks.length - setupDone}건 남았습니다
                            </span>
                        )}
                    </div>
                )}
                {composed.setupTasks.map((task, i) => (
                    <SetupTaskCard
                        key={task.id}
                        slot={task}
                        /* 회원 카드가 0건인 분기라 번호는 1 부터다 */
                        index={i}
                        checked={checkedSetup.has(task.id)}
                        onToggleCheck={handleToggleSetupCheck}
                        onHide={handleHideSetup}
                    />
                ))}
                {notGenerated ? (
                    <OpsEmpty
                        icon="🕘"
                        title={
                            isToday
                                ? '오늘 브리핑이 아직 만들어지지 않았습니다'
                                : `${dayWord}에는 브리핑이 만들어지지 않았습니다`
                        }
                        body={
                            /*
                             * "잠시 뒤에 다시 보세요"라고 쓰지 않는다.
                             * 서버는 공개 대상으로 지정된 센터에만 브리핑을 내보낸다
                             * (opsBriefing.scheduler.insertDay — 기본은 SHADOW).
                             * 오지 않을 수도 있는 것을 곧 온다고 말하지 않는다.
                             */
                            isToday
                                ? '브리핑은 매일 아침에 하루치를 모아 만듭니다. 아직 준비되지 않았습니다.'
                                : '그날은 브리핑이 생성되지 않아 남은 기록이 없습니다.'
                        }
                        hint="활성 회원 수도 함께 집계 전입니다. 0명이라는 뜻이 아닙니다."
                    />
                ) : setupAllDone ? (
                    /*
                     * 🔴 세팅 과제를 전부 체크한 날 (2026-09-01 QA29).
                     *    회원 카드가 0건이라 「할 일이 없습니다」로 떨어지면
                     *    방금 다섯 개를 체크한 원장님에게 **한 일이 없었다**고
                     *    말하는 셈이다. 끝냈다는 사실을 그 자리에 적는다.
                     */
                    <OpsEmpty
                        icon="✅"
                        title="오늘 세팅 할 일을 마쳤습니다"
                        body="남은 과제는 내일 아침 다시 정리해 드립니다. 설정이 실제로 끝나면 그 카드는 사라집니다."
                        hint="체크는 이 브라우저에서만 기억되고 날짜가 바뀌면 초기화됩니다."
                    />
                ) : composed.setupTasks.length > 0 ? (
                    /*
                     * 🔴 **위 카드와 자기모순이던 자리** (2026-09-01 QA24).
                     *
                     * 신규 센터는 회원 항목이 0건이라 이 빈 상태로 떨어지는데,
                     * 바로 위에 세팅 과제 카드가 서 있었다. 화면이 「할 일 2건」을
                     * 보여주면서 동시에 「오늘은 급하게 챙길 일이 없습니다」라고
                     * 말한 것이다. 원장님이 둘 중 무엇을 믿어야 할지 알 수 없다.
                     *
                     * 과제가 있으면 **없다고 말하지 않는다.** 회원 데이터가 아직
                     * 없다는 사실과, 그래서 오늘 할 일이 세팅이라는 사실을 잇는다.
                     */
                    <OpsEmpty
                        icon="🧭"
                        title={
                            isToday
                                ? '회원 데이터가 쌓이기 전까지는 센터 세팅이 오늘의 할 일입니다'
                                : `${dayWord}에는 센터 세팅이 할 일이었습니다`
                        }
                        body="위 과제를 끝내면 회원·강사 데이터가 쌓이기 시작하고, 그때부터 매일 아침 챙길 회원을 골라 드립니다."
                        hint="수강권을 등록한 회원이 생기고 수업 기록이 2주쯤 쌓이면 이탈 위험을 판단할 수 있습니다."
                    />
                ) : (
                    <OpsEmpty
                        icon="☕"
                        title={isToday ? '오늘은 급하게 챙길 일이 없습니다' : `${dayWord}에는 챙길 일이 없었습니다`}
                        body={
                            activeMembers > 0
                                ? '위험 신호가 잡힌 회원도, 흔들리는 강사도 없습니다. 이런 날 강사들과 이야기 나눠 보세요.'
                                : '수강권을 등록한 회원이 생기면 매일 아침 챙길 일을 정리해 드립니다.'
                        }
                        hint={
                            activeMembers > 0
                                ? undefined
                                : '회원 등록 후 수업 기록이 2주쯤 쌓이면 이탈 위험을 판단할 수 있습니다.'
                        }
                    />
                )}
                <OrderTracker />
                {/*
                  🔴 토스트도 이 분기에 있어야 한다 — 가리기의 「되돌리기」가
                     여기서만 쓸 수 있는 유일한 되돌림 경로다. 없으면 잘못 가린
                     과제를 사흘 동안 되돌릴 방법이 없다.
                */}
                {toast && (
                    <div
                        className="fixed left-1/2 flex items-center rounded-[10px] px-[20px] py-[14px]"
                        style={{
                            bottom: 40,
                            transform: 'translateX(-50%)',
                            background: '#1A305D',
                            color: '#FFFFFF',
                            fontSize: 16,
                            zIndex: 1000,
                            gap: 16,
                            boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
                        }}
                    >
                        <span>{toast.text}</span>
                        {toast.undo && (
                            <button
                                type="button"
                                onClick={() => {
                                    toast.undo?.run();
                                    setToast(null);
                                }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#8FE3CD',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                            >
                                {toast.undo.label}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-[16px] pb-[40px]">
            <DateNavigator value={date} onChange={setDate} label="시그널" />
            {isOpsBriefingMock() && <OpsMockBadge />}
            {!isToday && <PastDateBanner date={date} onBackToToday={() => setDate(dayjs().startOf('day'))} />}

            {/* ① 헤더 — 오늘의 판단을 한 문장으로 */}
            <div
                className="w-full overflow-hidden rounded-[16px]"
                style={{
                    background: 'linear-gradient(135deg, #1A305D 0%, #24406F 55%, #2B5183 100%)',
                    boxShadow: '0 10px 30px rgba(26,48,93,0.18)',
                }}
            >
                <div className="flex items-center justify-between" style={{ padding: '28px 32px' }}>
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13.5, color: '#9DB4D8', letterSpacing: '0.03em' }}>
                            {today.format('YYYY년 M월 D일')} {weekday}요일 · {isToday ? '손실 시그널' : '지난 시그널'}
                        </span>
                        <span
                            style={{
                                fontSize: 30,
                                fontWeight: 800,
                                color: '#FFFFFF',
                                marginTop: 8,
                                letterSpacing: '-0.03em',
                                lineHeight: '40px',
                            }}
                        >
                            {/*
                              ─────────────────────────────────────────────
                              머리말은 **편성 프레임**이다 — 아침 팝업과 같은 문법.
                              ─────────────────────────────────────────────
                              예전에는 여기서 전체 위험 수를 외쳤다("오늘 급하게 챙길
                              일이 28건 있습니다"). 그런데 바로 아래 목록은 이미
                              "급한 순으로 5건 · 외 26건"으로 접혀 있어서, 원장님은
                              28건이라고 들은 화면에서 다섯 줄을 본다.

                              28 은 정보가 아니라 겁주는 숫자다. 아침에 실제로 손이
                              가는 것은 서너 건이고, 나머지는 내일도 거기 있다
                              (MorningBriefingModal 과 briefingComposition 의 같은 판단).

                              그래서 **집중 건수가 주인공**이고 나머지는 보조로 밝힌다.
                              숫자는 아래 목록과 **같은 값**(composed.focus / restCount)을
                              쓴다. 다른 값을 쓰면 머리말과 목록이 다시 어긋난다 —
                              지금 고치는 바로 그 버그다.

                              전체 위험 수(28)를 버리지는 않는다. 아래 사실 줄에
                              「미처리 N건 · 위험 N건」으로 분모를 밝혀 적는다.
                            */}
                            {composed.focus.length > 0 ? (
                                <>
                                    {dayWord} 챙길 일{' '}
                                    {/*
                                      🔴 **진행률과 같은 값**이어야 한다 (2026-09-02 QA37).
                                         여기는 `composed.focus`(회원 카드)만 셌고 진행률은
                                         앞줄 카드까지 셌다. 실화면에서 헤더 「3건」과
                                         진행률 「5건」이 나란히 떴다 — 한 이름의 숫자가
                                         두 값을 가지면 원장님은 둘 다 못 믿는다.
                                         모집단은 **화면에 서는 카드**로 통일한다.
                                    */}
                                    <span style={{ color: '#FF8A92' }}>
                                        {trackedCount}건
                                    </span>
                                    {restCount > 0 && (
                                        <span
                                            style={{
                                                fontSize: 19,
                                                fontWeight: 700,
                                                color: '#9DB4D8',
                                                letterSpacing: '-0.02em',
                                                marginLeft: 11,
                                            }}
                                        >
                                            · 외 {restCount}건
                                        </span>
                                    )}
                                </>
                            ) : (
                                summaryLine
                            )}
                        </span>
                    </div>

                    {/*
                      우측 요약 — 금액이 먼저 보여야 한다.

                      ⚠️ 0 원인 칸은 그리지 않는다. 헤더에서 가장 큰 글씨(27px)를 쓰는 자리라
                      "0" 이 매일 떠 있으면 정작 숫자가 있는 날에도 눈에 안 들어온다.
                      금액을 **모르는** 건은 여기서 0 으로 뭉개지지 않는다 —
                      아래 줄의 '금액 미산정 N건' 이 따로 말한다.

                      ─────────────────────────────────────────────────────────────
                      🔴 이 두 금액은 **그날 올라온 카드 몫**이다 (2026-08-31 대표 확정 ⓐ)
                      ─────────────────────────────────────────────────────────────
                      확정본 M2/M4 는 「전 회원」이라고 적혀 있다. 그 모집단은
                      **회원 현황 헤더가 담당한다** — 거기가 센터 전체(2.7억대)를 말한다.

                      오늘의 브리핑은 「오늘 챙길 일」로 **선별한 화면**이라 헤더도
                      오늘 몫이 맞다. 전 회원 상수를 매일 같은 자리에 띄우면
                        · 매일 거의 안 변하는 큰 숫자라 소음이 되고
                        · 회원 현황과 같은 값이 두 화면에 중복으로 뜬다.

                      대신 **범위를 라벨로 못 박는다.** 같은 이름의 숫자가 두 화면에서
                      두 자릿수 배 차이인데 범위가 안 적혀 있으면, 원장님은 둘 중 하나를
                      고장으로 읽는다. 아래 `scopeNote` 가 그 자리다.
                    */}
                    <div className="flex items-center" style={{ gap: 0 }}>
                        {[
                            {
                                label: `${dayWord} 지켜야 할 매출`,
                                icon: '💰',
                                value: prepaidAmount,
                                tone: '#FFC9CD',
                                hint:
                                    `${PREPAID_DEFINITION}\n\n` +
                                    `${dayWord} 브리핑에 올라온 회원 카드만 셉니다. ` +
                                    '센터 전체는 회원 현황에서 봅니다.',
                                open: () => setValueModal({ kind: 'TOTAL', axis: 'PREPAID' }),
                            },
                            {
                                label: '확보 가능한 매출',
                                icon: '📈',
                                value: expectedAmount,
                                tone: '#8FE3CD',
                                hint:
                                    `${EXPECTED_DEFINITION}\n\n` +
                                    `${dayWord} 브리핑에 올라온 회원 카드만 셉니다. ` +
                                    '센터 전체는 회원 현황에서 봅니다.',
                                open: () => setValueModal({ kind: 'TOTAL', axis: 'EXPECTED' }),
                            },
                        ]
                            .filter((stat) => stat.value > 0)
                            .map((stat, i) => (
                            <div
                                key={stat.label}
                                className="flex flex-col"
                                style={{
                                    padding: '0 26px',
                                    borderLeft: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.14)',
                                }}
                            >
                                <span
                                    title={stat.hint}
                                    style={{ fontSize: 12.5, color: '#9DB4D8', cursor: 'help' }}
                                >
                                    {stat.icon} {stat.label}
                                </span>
                                {/*
                                  🔴 금액을 누르면 **누가 얼마씩 들어갔는지**가 펴진다 (QA31).
                                     툴팁은 정의만 말하고 내역은 못 말한다 — 답 못 하는
                                     금액은 결국 안 믿게 된다. 시각은 점선 밑줄뿐이다.
                                */}
                                <div className="flex items-baseline" style={{ gap: 3, marginTop: 5 }}>
                                    <ValueTrigger
                                        label={`${stat.label} 산출 근거 보기`}
                                        onClick={stat.open}
                                        style={{
                                            fontSize: 27,
                                            fontWeight: 800,
                                            color: stat.tone,
                                            letterSpacing: '-0.03em',
                                        }}
                                    >
                                        {won(stat.value)}
                                    </ValueTrigger>
                                    <span style={{ fontSize: 13, color: '#9DB4D8' }}>원</span>
                                </div>
                                {/*
                                  범위 서브라벨. 짧게 — 큰 숫자 밑에 문장이 붙으면
                                  숫자가 안 읽힌다. 자세한 설명은 위 라벨 툴팁이 맡는다.
                                */}
                                <span
                                    style={{
                                        fontSize: 11.5,
                                        color: '#8FA5C4',
                                        marginTop: 2,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {scopeNote}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 헤더 하단 — 리스크 + 편성 근거 */}
                <div
                    className="flex items-center justify-between"
                    style={{
                        padding: '13px 32px',
                        borderTop: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(0,0,0,0.12)',
                    }}
                >
                    {/*
                      🔴 「잠재 리스크」를 뺐다 (2026-08-31 정책 M11).
                         `kind: 'RISK'` 를 만드는 코드가 이 저장소 어디에도 없었고
                         서버도 채우지 않아 **늘 0원**이었다. 0원이라 화면에 안 뜨는
                         칸이 코드에만 살아 있으면, 다음 사람은 그게 동작하는 지표인 줄
                         알고 그 위에 계산을 얹는다. 타입(ValueKind)에서도 함께 지웠다.
                    */}
                    <span style={{ fontSize: 13.5, color: '#B7C7E0' }}>
                        {headerFacts.join(' · ')}
                        {unpricedCount > 0 && (
                            /* 합계에 안 들어간 건이 있으면 밝혀야 숫자를 믿을 수 있다 */
                            <>
                                {headerFacts.length > 0 && '  ·  '}
                                <span
                                    style={{ color: '#8FA5C4' }}
                                    title="단가가 없는 수강권이거나 금액이 걸리지 않는 항목입니다"
                                >
                                    금액 미산정 {unpricedCount}건
                                </span>
                            </>
                        )}
                    </span>
                    {/*
                      🔴 편성만 말하는 문장은 안 그린다 (2026-09-01 QA32).

                      「위험 N건은 상한과 무관하게 모두 브리핑에 올렸습니다」는
                      상한 이야기가 아니라 편성 이야기다. 대표 지시로 오늘의
                      브리핑에서 설명글을 걷어내면서 이 갈래도 함께 뺐다.

                      ⚠️ 판정은 `briefingPolicy` 가 한다 — 여기서 문자열을 비교하면
                         문구를 한 글자만 고쳐도 조용히 다시 뜬다.
                      ⚠️ 「가린 회원 N명 제외」는 **남긴다.** 편성 설명이 아니라
                         합계에서 빠진 사실이라, 안 밝히면 원장님이 자기가 가려 놓고
                         「오늘은 조용하네」로 읽는다.
                    */}
                    <span style={{ fontSize: 12.5, color: '#8FA5C4' }} title={quota?.description}>
                        {quotaNote.compositionOnly ? quotaNote.hiddenNote : quotaNote.text}
                    </span>
                </div>

                {/*
                  🔴 설명글 두 덩어리를 화면에서 뺐다 (2026-09-01 QA32, 대표 지시).

                    · 「지켜야 할 매출은 …」 정책 설명 문단
                    · 「재등록 기회 후보 N건 중 N건만 올랐습니다 / 휴면 N건은 자리가
                      없어 …」 편성 안내 (explainComposition)

                  ⚠️ **계산은 그대로 살아 있다.** `explainComposition` 도, 그 셈을 쓰는
                     지난 리포트도 안 건드렸다. 오늘의 브리핑 헤더가 아침 30초 안에
                     읽혀야 하는 자리라, 문단 셋이 숫자를 덮고 있었던 것이 문제였다.

                  ⚠️ 정책 설명은 **없어진 게 아니라 팝업으로 옮겼다** (QA31).
                     헤더 금액을 누르면 정의·구성 내역·정책 문구가 함께 뜬다.
                     그래서 「보수적으로 잡았다」·「위험·주의는 0」 같은 말이
                     사라지지 않고, 그 숫자를 궁금해한 사람에게만 보인다.
                */}
            </div>

            {/*
              밀린 지시가 있는 날은 진행 현황이 **오늘 챙길 일보다 위**에 온다.
              어제 이전에 낸 지시가 아직 안 끝난 것은 이미 늦은 것이고,
              새로 올라온 일보다 먼저 손대야 한다. (자리만 바뀐다 — 내용은 그대로다)
            */}
            {hasBacklog && trackerSection}

            {/* ② 오늘 챙길 일 */}
            <div>
                <div className="mb-[12px] flex items-baseline justify-between">
                    <div className="flex items-baseline" style={{ gap: 10 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#000000' }}>
                            {isToday ? '오늘 챙길 일' : `${dayWord} 브리핑에 올라온 일`}
                        </span>
                        {/*
                          왜 다섯 건인지 밝힌다.
                          접었다는 사실을 말하지 않으면 원장님은 "오늘은 다섯 건뿐이구나" 로
                          읽는다 — 편성 안내(quotaNote)를 헤더에 두는 것과 같은 이유다.
                        */}
                        {restCount > 0 && (
                            /*
                              ⚠️ 여기서 건수를 다시 적지 않는다.
                              머리말이 이미 「챙길 일 5건 · 외 26건」이라고 말했고,
                              같은 수를 40px 아래에서 한 번 더 말하면 둘 다 안 읽힌다
                              (진행률 줄에서 금액을 뺀 것과 같은 판단).
                              이 자리가 맡을 몫은 **왜 이 다섯 건인가** 하나다.
                            */
                            <span style={{ fontSize: 13.5, color: '#5B6B7F' }}>
                                급한 순으로 골랐습니다
                            </span>
                        )}
                        {/* 누를 것이 남아 있을 때만 사용법을 적는다 — 다 끝난 날에는 할 말이 아니다 */}
                        {pending.length > 0 && (
                            <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                                카드를 누르면 보낼 문구와 지시 버튼이 열립니다
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => window.dispatchEvent(new Event(OPEN_MORNING_BRIEFING_EVENT))}
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: 14, color: '#5B6B7F', textDecoration: 'underline', textUnderlineOffset: 3, padding: 0,
                        }}
                    >
                        아침 팝업 다시 보기
                    </button>
                </div>

                {/* 진행률 — 몇 건 남았는지가 목록보다 먼저 보여야 한다 */}
                {trackedCount > 0 && (
                    <div
                        aria-label="오늘 진행률"
                        className="mb-[12px] w-full rounded-[12px] border border-solid bg-white"
                        style={{ borderColor: '#E3EAF3', padding: '14px 22px' }}
                    >
                        <div className="flex items-baseline justify-between">
                            <div className="flex items-baseline" style={{ gap: 8 }}>
                                <span style={{ fontSize: 22, fontWeight: 800, color: '#16202E', letterSpacing: '-0.02em' }}>
                                    {actedInFocus}
                                </span>
                                {/*
                                  🔴 분모는 **머리말의 그 다섯 건**이다 (2026-08-28 대표 결정).

                                  예전에는 그날 올라온 전부(30건)를 분모로 썼다. 그러면
                                  머리말은 「챙길 일 5건」인데 진행률은 「전체 30건」이라
                                  같은 화면의 두 숫자가 다른 것을 세고, 원장님은 다섯 건을
                                  다 처리하고도 미달로 읽는다.
                                  「외 N건」은 오늘 할 일이 아니라 추가 브리핑이다.

                                  강사 인사이트는 완료 체크를 걸 수 없어 분모에서 빠진다 —
                                  넣으면 100% 에 영영 못 닿는다.

                                  🔴 **세팅 과제는 이제 센다** (2026-09-01 QA29).
                                     체크할 수 있게 됐고, 무엇보다 신규 센터는 회원 카드가
                                     0건이라 그걸 빼면 분모가 0 이 되어 진행률이 영원히
                                     의미를 못 가졌다.
                                */}
                                <span
                                    style={{ fontSize: 15, color: '#7C8794' }}
                                    title="오늘 챙길 일로 세운 건만 셉니다. 「외 N건」과 가린 건은 빠지고, 체크할 수 없는 강사 인사이트도 빠집니다."
                                >
                                    / 오늘 챙길 일 {trackedCount}건 처리
                                </span>
                                {/*
                                  🔴 **분모와 같은 모집단**이어야 한다 (2026-09-01 QA29 후속).
                                     `pendingInFocus` 는 회원 카드만 세는 옛 값이라,
                                     세팅 과제가 분모에 들어온 뒤로 「0 / 4건 처리 ·
                                     아직 2건」 같은 모순이 찍혔다. 한 줄 안에서
                                     두 숫자가 다른 것을 세면 원장님은 둘 다 못 믿는다.

                                     분모 = 처리 + 남음 이라는 항등식으로 묶는다.
                                */}
                                {remainingCount > 0 && (
                                    <span style={{ fontSize: 14.5, color: '#C22D38', marginLeft: 6 }}>
                                        아직 {remainingCount}건 남았습니다
                                    </span>
                                )}
                                {/* 가린 건은 처리한 것도 남은 것도 아니다. 셈에서 뺐다고 밝힌다 */}
                                {dismissedCount > 0 && (
                                    <span style={{ fontSize: 13.5, color: '#93A0AE', marginLeft: 6 }}>
                                        가린 {dismissedCount}건 제외
                                    </span>
                                )}
                            </div>
                            {/*
                              여기 "남은 건에 걸린 매출 N원" 이 있었다. 지운 게 아니라
                              **바로 위 헤더에 이미 있는 숫자**였다 —
                              defendAmount + gainAmount 는 헤더 두 칸의 합 그대로다.
                              같은 수를 40px 아래에서 한 번 더 말하면 둘 다 안 읽힌다.
                              금액은 헤더가, 이 줄은 진행(몇 건 남았나)만 맡는다.
                            */}
                        </div>
                        <div
                            className="rounded-full"
                            style={{ height: 8, background: '#EDF1F7', marginTop: 10, overflow: 'hidden' }}
                        >
                            <div
                                className="rounded-full"
                                style={{
                                    width: `${Math.round((actedInFocus / trackedCount) * 100)}%`,
                                    height: 8,
                                    background: '#00A793',
                                    transition: 'width .3s',
                                }}
                            />
                        </div>
                    </div>
                )}
                {/*
                  ⚠️ 완료 패널은 목록을 **대체하지 않고** 위에 얹는다.
                  예전에는 pending.length === 0 일 때 이 패널로 목록을 통째로 갈아치웠다.
                  마지막 한 건을 처리하는 순간 카드가 전부 사라져서,
                  잘못 눌렀어도 '되돌리기'를 누를 카드 자체가 없었다.
                  UI 스펙 U6("완료가 사라지지 않음 — 접히되 남음")과
                  BriefingCard 의 ACTED 접힘 상태(되돌리기 링크 포함)가 이쪽이 맞다고 말한다.

                  하드코딩 이력: 여기 "어제 수업 38건 · 노쇼 2건 · 신규 회원 1명" 이 박혀 있었다.
                  목 상수라 어느 센터에서든 같은 숫자가 떴고, 서버 응답에는 그 값이 없다.
                  0 으로 채우면 "어제 수업이 한 건도 없었다"는 거짓이 되므로 아예 뺐다.
                  필요하면 서버에 요청한다: BACKEND_HANDOFF_OPS_BRIEFING_ACTIONS.md
                */}
                {pending.length === 0 && (
                    <div
                        className={`${CARD_BOX} mb-[12px] flex items-center px-[24px] py-[18px]`}
                        style={{ gap: 12, borderColor: '#B7DFD2', background: '#F4FCF9' }}
                    >
                        <span
                            className="flex flex-none items-center justify-center rounded-full"
                            style={{ width: 26, height: 26, background: '#00A793', color: '#fff', fontSize: 15, fontWeight: 800 }}
                        >
                            ✓
                        </span>
                        <span style={{ fontSize: 16.5, fontWeight: 700, color: '#00795F' }}>
                            {isToday
                                ? '오늘 올라온 일은 모두 처리하셨습니다'
                                : `${dayWord} 브리핑은 모두 처리되었습니다`}
                        </span>
                        <span style={{ fontSize: 14, color: '#5D9384' }}>
                            이런 날 강사들과 이야기 나눠보세요
                        </span>
                    </div>
                )}
                {/*
                  오늘 챙길 일 — **카드 목록**.

                  카드가 그리는 것은 데이터뿐이다(actions='none').
                  강사에게 요청하는 부분은 카드를 눌러 여는 상세에만 있다.
                  같은 BriefingCard 한 벌로 목록과 상세를 다 그린다 — 포크하지 않는다.
                */}
                <div className="flex flex-col gap-[12px]">
                    {/*
                      ─────────────────────────────────────────────────────
                      세팅·정착 과제 — **목록 위 별도 블록** (2026-08-28 결정)
                      ─────────────────────────────────────────────────────
                      다섯 자리를 쓰지 않는다. 자리를 가져가게 뒀더니 화면마다
                      「5건」의 뜻이 갈렸다(오늘은 회원 3~4건, 지난 리포트는 회원 5건).
                      이제 회원 다섯 건이 온전히 서고 번호 1~5도 회원 카드만 쓴다.
                    */}
                    {/*
                      🔴 **강사 카드가 있으면 그것이 이긴다** (2026-09-01 대표 시안).

                      과도기 센터의 세팅 안내를 축 단위(「회원 앱 설치 151명」)에서
                      **강사 단위**로 바꿨다. 원장님이 실제로 연락하는 대상이
                      축이 아니라 사람이기 때문이다.

                      ⚠️ 자리 규칙은 그대로다 — 회원이 못 채운 자리만큼만.
                      ⚠️ `coachOnboarding` 이 없으면(배포 전·404) 빈 배열이라
                         아래 예전 축 카드로 그대로 떨어진다.
                    */}
                    {transitionCoachCards.length > 0
                        ? transitionCoachCards.map((view, i) => (
                              <CoachOnboardingCard
                                  key={view.row.coachId}
                                  view={view}
                                  index={i}
                                  /* 진행률 분자와 같은 상태를 본다 — 카드마다 다시 풀지 않는다 */
                                  action={actionStateOf(coachActionStates, view.row.coachId)}
                                  serverBacked={coachOnboarding?.actions != null}
                                  onOpenMember={openMemberAction}
                                  onCheck={(id) => {
                                      void actOnCoach(id, 'CHECK', view.row.name);
                                  }}
                                  onHide={(id) => {
                                      void actOnCoach(id, 'HIDE_TODAY', view.row.name);
                                  }}
                              />
                          ))
                        : composed.setupTasks.map((task, i) => (
                              <SetupTaskCard
                                  key={task.id}
                                  slot={task}
                                  /*
                                   * 🔴 과제 카드가 **위**에 서므로 1부터다.
                                   *    회원 카드는 그만큼 밀린다(`displayOrder`).
                                   */
                                  index={i}
                                  checked={checkedSetup.has(task.id)}
                                  onToggleCheck={handleToggleSetupCheck}
                                  onHide={handleHideSetup}
                              />
                          ))}

                    {composed.focus.map((slot) => (
                        <BriefingCard
                            key={slot.item.itemId}
                            item={slot.item}
                            index={orderOf(slot.item.itemId)}
                            actions="none"
                            willCreateOrder={canCreateOrder(
                                slot.item,
                                slot.item.recommendation.actionCode
                            )}
                            onAction={handleAction}
                            onMarkDone={handleMarkDone}
                            onUndo={handleUndo}
                            onShowRaw={setRawItemId}
                            /* 금액 태그를 누르면 이 건의 산출 근거를 편다 (QA31) */
                            onShowValue={(itemId, axis) =>
                                setValueModal({ kind: 'ITEM', itemId, axis })
                            }
                            onOpenDetail={setOpenItemId}
                            onHide={handleHide}
                            locked={rowsLocked}
                            selected={slot.item.itemId === openItemId}
                            /*
                              이 회원 건으로 이미 나가 있는 지시.
                              토스트는 사라지므로 "보냈다"는 사실이 목록에 남아 있어야 한다.
                            */
                            sentOrder={
                                slot.item.subjectType === 'MEMBER' && slot.item.subjectId
                                    ? confirm.sentOrderFor(slot.item.subjectId)
                                    : null
                            }
                            busy={confirm.sending || setItemDone.isLoading}
                        />
                    ))}

                    {/*
                      강사 인사이트 — **목록 아래 별도 줄** (원래 자리).
                      회원 건은 오늘 안에 연락이 나가야 하는 일이고 강사 건은
                      면담 자리를 잡는 일이라, 섞어 세우면 「지금 전화할 사람」
                      명단을 훑는 흐름이 중간에 끊긴다.
                    */}
                    {composed.coachInsight && (
                        <CoachInsightCard
                            key={`coach-${composed.coachInsight.analysis.coach.coachId}`}
                            analysis={composed.coachInsight.analysis}
                            selected={composed.coachInsight.analysis.coach.coachId === openCoachId}
                            /* 카드 본문 → 데이터 드로어, 버튼 → 곧장 인쇄물 */
                            onOpen={setOpenCoachId}
                            onOpenReport={setReportCoachId}
                        />
                    )}

                    {/*
                      ═══════════════════════════════════════════════════════
                      🔴 「외 N건」을 **여기서 나열하지 않는다** (2026-08-28 대표 결정)
                      ═══════════════════════════════════════════════════════
                      예전에는 접어 두고 펼치면 전체 순위 목록이 그대로 나왔다.
                      그런데 그 항목들은 **회원 현황·강사 현황 탭에 이미 전부**
                      있다. 같은 목록을 브리핑에서 한 번 더 늘어놓으면
                      「급한 것만 골라 준다」는 이 화면의 값이 사라진다 —
                      브리핑의 값은 나열이 아니라 **선별**에 있다.

                      ⚠️ **없앤 것이 아니라 옮긴 것이다.** 그래서 몇 건이 남았는지와
                         어디로 가면 되는지를 반드시 적는다. 안 적으면
                         「오늘은 다섯 건뿐이구나」로 읽힌다.

                      ⚠️ 가리기 승계는 그대로다. focus 항목을 가리면 편성 함수가
                         DISMISSED 를 먼저 걸러내므로 **다음 순위가 자동으로
                         그 자리에 올라온다**(composeBriefing 의 splitFocus 인자).
                         서버 문서의 여섯 번째 이후 항목이 그 대기열이다.
                    */}
                    {restCount > 0 && (
                        <div
                            className="flex w-full items-center justify-center rounded-[12px] border border-solid"
                            style={{
                                minHeight: 46,
                                borderColor: '#E3EAF3',
                                background: '#FBFCFE',
                                color: '#5B6B7F',
                                fontSize: 13.5,
                                padding: '12px 16px',
                                gap: 6,
                                flexWrap: 'wrap',
                            }}
                        >
                            <span>이 외 {restCount}건은</span>
                            <Link
                                to="/opsBriefing/members"
                                style={{ color: '#3D73DD', fontWeight: 700 }}
                            >
                                회원 현황
                            </Link>
                            <span>·</span>
                            <Link
                                to="/opsBriefing/coaches"
                                style={{ color: '#3D73DD', fontWeight: 700 }}
                            >
                                강사 현황
                            </Link>
                            <span>에서 볼 수 있습니다</span>
                        </div>
                    )}
                </div>

                {/*
                  가린 항목.

                  목록에서는 빠지되 **없던 일이 되지는 않는다.** 되돌리기 토스트는
                  몇 초 뒤 사라지므로, 그것만으로는 원장님이 자기가 무엇을 가렸는지
                  다시 볼 방법이 없다. 서버에 DISMISSED 로 남아 있는 사실이라
                  화면에도 꺼내 볼 길을 둔다 (철회 서랍과 같은 생각).
                */}
                {dismissedCount > 0 && (
                    <div style={{ marginTop: 10 }}>
                        <button
                            type="button"
                            onClick={() => setShowDismissed((v) => !v)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 13.5,
                                color: '#93A0AE',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                            }}
                        >
                            가린 항목 {dismissedCount}건 {showDismissed ? '접기' : '보기'}
                        </button>
                        {showDismissed && (
                            <div
                                className="w-full rounded-[10px] border border-solid"
                                style={{
                                    borderColor: '#E3EAF3',
                                    background: '#FBFCFE',
                                    marginTop: 8,
                                    padding: '6px 14px',
                                }}
                            >
                                {composed.dismissed.map((slot) => (
                                    <div
                                        key={slot.item.itemId}
                                        className="flex items-center justify-between"
                                        style={{ padding: '9px 0', gap: 12 }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 13.5,
                                                color: '#7C8794',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {slot.item.subjectName} · {slot.item.title}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleRestore(slot.item.itemId)}
                                            disabled={setItemStatus.isLoading}
                                            aria-label={`${slot.item.subjectName} 건 다시 보기`}
                                            style={{
                                                flex: 'none',
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: setItemStatus.isLoading ? 'progress' : 'pointer',
                                                fontSize: 13,
                                                color: '#3D73DD',
                                                textDecoration: 'underline',
                                                textUnderlineOffset: 3,
                                                padding: 0,
                                            }}
                                        >
                                            다시 보기
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ③ 지시 및 진행 현황 — 밀린 것이 없는 날은 여기(오늘 챙길 일 아래) */}
            {!hasBacklog && trackerSection}

            {showCoachApp && (
                <CoachTaskMobilePreview
                    orders={orders ?? []}
                    loading={ordersLoading}
                    onClose={() => setShowCoachApp(false)}
                />
            )}

            {/*
              브리핑 상세 — 목록이 원래 쓰던 **카드를 그대로** 담는다.
              여기서 다시 그리면 목록을 열었을 때와 안 열었을 때가 갈린다.
              지시하기·문구 고치기·근거 보기가 전부 이 안에서 그대로 동작한다.
            */}
            {openedItem && (
                <BriefingDetailDrawer
                    item={openedItem}
                    onClose={() => setOpenItemId(null)}
                    /*
                      확인 창이 위에 떠 있으면 Esc 는 그 창만 닫아야 한다.
                      둘 다 닫히면 취소를 눌렀을 뿐인데 보던 항목까지 잃는다.
                    */
                    escapeLocked={confirm.modal != null || valueModal != null}
                >
                    <BriefingCard
                        item={openedItem}
                        /* 목록에서 몇 번째 카드였나 — 방금 누른 카드와 같은 번호여야 한다 */
                        index={orderOf(openedItem.itemId)}
                        /* 상세에는 강사에게 요청하는 부분까지 전부 그린다 (기본값) */
                        actions="full"
                        willCreateOrder={canCreateOrder(
                            openedItem,
                            openedItem.recommendation.actionCode
                        )}
                        onAction={handleAction}
                        // 체크박스는 완료 기록만. 지시는 주 CTA 만 만든다 (handleMarkDone 주석 참고)
                        onMarkDone={handleMarkDone}
                        onUndo={handleUndo}
                        onShowRaw={setRawItemId}
                        /* 금액 태그를 누르면 이 건의 산출 근거를 편다 (QA31) */
                        onShowValue={(itemId, axis) =>
                            setValueModal({ kind: 'ITEM', itemId, axis })
                        }
                        sentOrder={
                            openedItem.subjectType === 'MEMBER' && openedItem.subjectId
                                ? confirm.sentOrderFor(openedItem.subjectId)
                                : null
                        }
                        /*
                          선물 버튼만 조건이 다르다 — 포인트가 나가는 동작이다.
                          보낼 수 없는 계정에서는 그리지 않고, 지난 날짜에서는 잠근다.
                          (submit 시점에도 다시 검사한다 — BriefingGiftModal 의 GiftSendSession)
                        */
                        giftGate={{
                            hidden: !gift.allowed,
                            disabled: !isToday,
                            reason: '지난 날짜에서는 선물을 보낼 수 없습니다',
                        }}
                        sentGift={
                            openedItem.subjectType === 'MEMBER' && openedItem.subjectId
                                ? gift.sentGiftFor(openedItem.subjectId)
                                : null
                        }
                        busy={confirm.sending || setItemDone.isLoading || gift.sending}
                    />
                </BriefingDetailDrawer>
            )}

            {/*
              강사 카드의 상세 — **데이터 드로어가 먼저**, 리포트는 그 안의 버튼으로.

              드로어에 넘기는 값은 전부 이 화면이 이미 계산해 둔 것이다
              (coachAnalyses = analyzeAll(coachRows, centerAvg)). 드로어가 스스로
              다시 계산하면 같은 강사가 드로어와 리포트에서 다른 등급을 받는다 —
              옛 CoachDetailDrawer 를 지운 이유가 정확히 그것이다.

              peers 로 **coachRows 전체**를 넘긴다. 일부만 넘기면 "N명 중 M위"가
              강사 현황 탭과 갈린다.
            */}
            {openedCoachAnalysis && (
                <CoachDataDrawer
                    analysis={openedCoachAnalysis}
                    peers={coachRows}
                    month={date.format('YYYY-MM')}
                    convMid={centerAvg.conversionPct}
                    /* 세로축은 일지 작성률 — 강사 현황 산점도와 같은 기준선 */
                    careMid={centerAvg.journalPct}
                    onClose={() => setOpenCoachId(null)}
                    onOpenReport={() => setReportCoachId(openedCoachAnalysis.coach.coachId)}
                />
            )}

            {/*
              월간 리포트 — 강사 현황 탭과 **같은 리포트**다.
              여기서 요약본을 따로 그리면 두 화면의 판정이 갈린 것처럼 보인다.
              (센터 평균·동료·회원 명부는 CoachReportSheet 가 같은 쿼리 키로
               직접 받아 온다 — 화면마다 다른 값을 넘겨 등급이 갈리지 않게.)

              데이터 드로어는 **닫지 않는다.** 리포트를 덮어 띄우고, 리포트를 닫으면
              보고 있던 강사 데이터로 돌아온다 — 리포트만 확인하러 들어갔다가
              보던 화면까지 잃으면 원장님이 카드를 다시 찾아 눌러야 한다.
            */}
            {reportCoach && (
                <CoachReportSheet
                    coach={reportCoach}
                    month={date.format('YYYY-MM')}
                    onClose={() => setReportCoachId(null)}
                />
            )}

            {/* 원천 데이터 팝업 — 서버에서 받아 온다 */}
            {rawItemId && (
                <EvidenceModal
                    itemId={rawItemId}
                    briefingDate={dateKey}
                    onClose={() => setRawItemId(null)}
                />
            )}

            {/*
              왜 이 금액인가 — 헤더 금액과 카드 배지가 이 창을 연다 (QA31).

              🔴 **합계는 헤더가 그린 그 변수 그대로**이고, 목록은 그 합계를 만든
                 바로 그 목록(`briefingValue` 의 contributors)이다. 여기서 다시
                 걸러 내면 내역을 더한 값이 헤더와 안 맞을 수 있다.
                 두 축은 분모가 다르다 — 선수금은 항목 전부, 기대는 회원당 한 번.
            */}
            {valueModal?.kind === 'TOTAL' && valueModal.axis === 'PREPAID' && (
                <ValueBreakdownModal
                    title={`${dayWord} 지켜야 할 매출`}
                    definition={PREPAID_DEFINITION}
                    scopeNote={scopeNote}
                    rows={itemPrepaidRows(prepaidContributors(pending))}
                    total={prepaidAmount}
                    unavailable={{ count: unpricedCount, reason: UNPRICED_REASON }}
                    policyNotes={[
                        ...PREPAID_NOTES,
                        `${dayWord} 브리핑에 올라온 회원 카드만 셉니다. 센터 전체는 회원 현황에서 봅니다.`,
                    ]}
                    onClose={() => setValueModal(null)}
                />
            )}
            {valueModal?.kind === 'TOTAL' && valueModal.axis === 'EXPECTED' && (
                <ValueBreakdownModal
                    title="확보 가능한 매출"
                    definition={EXPECTED_DEFINITION}
                    scopeNote={scopeNote}
                    rows={itemExpectedRows(expectedContributors(pending))}
                    total={expectedAmount}
                    unavailable={{ count: unpricedCount, reason: UNPRICED_REASON }}
                    policyNotes={[
                        ...expectedNotes('이탈 위험·휴면 항목'),
                        `${dayWord} 브리핑에 올라온 회원 카드만 셉니다. 센터 전체는 회원 현황에서 봅니다.`,
                    ]}
                    onClose={() => setValueModal(null)}
                />
            )}
            {/*
              카드 금액 태그 — 그 한 건의 근거.
              항목은 목록에서 다시 찾는다. 못 찾으면 안 연다 — 다시 받아 온 응답에
              그 항목이 없어졌다는 뜻이고, 낡은 복사본을 보여주는 것보다 낫다.
            */}
            {valueModal?.kind === 'ITEM' &&
                (() => {
                    const target = items.find((i) => i.itemId === valueModal.itemId);
                    if (!target) return null;
                    const isPrepaid = valueModal.axis === 'PREPAID';
                    return (
                        <ValueBreakdownModal
                            single
                            title={isPrepaid ? '미사용 선수금' : '재등록 기대'}
                            definition={isPrepaid ? PREPAID_DEFINITION : EXPECTED_DEFINITION}
                            scopeNote={`${target.subjectName}${
                                target.membership?.product ? ` · ${target.membership.product}` : ''
                            } 한 건 기준`}
                            rows={
                                isPrepaid ? itemPrepaidRows([target]) : itemExpectedRows([target])
                            }
                            total={
                                (isPrepaid ? target.value?.prepaid : target.value?.expected) ?? 0
                            }
                            policyNotes={
                                isPrepaid ? PREPAID_NOTES : expectedNotes('이탈 위험·휴면 항목')
                            }
                            onClose={() => setValueModal(null)}
                        />
                    );
                })()}

            {/* 지시 보내기 전 확인 — 회원 현황 화면과 같은 창이다 */}
            {confirm.modal}

            {/* 선물 보내기 — 회원 현황 화면과 같은 창이고, 2차 비밀번호 창까지 이 안에 있다 */}
            {gift.modal}

            {/* 토스트 */}
            {toast && (
                <div
                    className="fixed left-1/2 flex items-center rounded-[10px] px-[20px] py-[14px]"
                    style={{
                        bottom: 40,
                        transform: 'translateX(-50%)',
                        background: '#1A305D',
                        color: '#FFFFFF',
                        fontSize: 16,
                        zIndex: 1000,
                        gap: 16,
                        boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
                    }}
                >
                    <span>{toast.text}</span>
                    {/*
                      되돌릴 수 있는 동작은 **그 자리에서** 되돌린다.
                      "가렸습니다" 만 띄우고 되돌릴 길을 목록 아래로만 두면,
                      잘못 눌렀다는 것을 알아챈 순간 어디를 눌러야 하는지 모른다.
                    */}
                    {toast.undo && (
                        <button
                            type="button"
                            onClick={() => {
                                const run = toast.undo?.run;
                                setToast(null);
                                run?.();
                            }}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#9DC2FF',
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                                flex: 'none',
                            }}
                        >
                            {toast.undo.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default OpsBriefingTodayPage;
